import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  StyleSheet,
  View,
  FlatList,
  ActivityIndicator,
  Alert,
  Platform,
  TouchableOpacity,
  Text,
  AppState,
  Linking,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import * as NavigationBar from 'expo-navigation-bar';
import { Provider as PaperProvider, Card, Title, Paragraph, Chip, FAB, IconButton, Avatar, MD3DarkTheme, Menu } from 'react-native-paper';
import { Audio } from 'expo-av';
import { useKeepAwake } from 'expo-keep-awake';
import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import io from 'socket.io-client';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useFonts } from 'expo-font';
import * as Updates from 'expo-updates';
import {
  DEFAULT_AVATAR_OPTION,
  normalizeAvatar,
  AVATAR_TYPES,
} from './constants/avatars';
import {
  ensureAdsenseInitialized,
  showWebRewardedInterstitial,
  isAdsenseConfigured,
} from './utils/webRewardedAd';
import AboutPage from './components/AboutPage';
import ContactPage from './components/ContactPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import EditorialContent from './components/EditorialContent';
import TopLists from './components/TopLists';
import LyricsShowcase from './components/LyricsShowcase';
import TurkishPopArtists from './components/TurkishPopArtists';

// API Base URL - Railway kullanarak tüm bağlantı sorunlarını çözeriz
const RAILWAY_URL = 'https://radyohub-production.up.railway.app';
const FAVORITES_STORAGE_KEY = '@radyohub/favorites';
const RECENTS_STORAGE_KEY = '@radyohub/recentRadios';
const TOKENS_STORAGE_KEY = '@radyohub/tokens';
const DAILY_FREE_ENERGY_KEY = '@radyohub/dailyFreeEnergy';
const isWebPlatform = Platform.OS === 'web';

let RewardedAd = null;
let RewardedAdEventType = null;
let AdEventType = null;
let TestIds = null;
let mobileAdsModule = null;

if (!isWebPlatform) {
  try {
    const moduleName = 'react-native-google-mobile-ads';
    const dynamicRequire = eval('require'); // eslint-disable-line no-eval
    const mobileAds = dynamicRequire(moduleName);
    mobileAdsModule = mobileAds;
    RewardedAd = mobileAds.RewardedAd;
    RewardedAdEventType = mobileAds.RewardedAdEventType;
    AdEventType = mobileAds.AdEventType;
    TestIds = mobileAds.TestIds;
  } catch (error) {
    console.warn('react-native-google-mobile-ads cannot be loaded:', error);
  }
}
// Platform'a göre AdMob Rewarded Ad Unit ID'leri
// Manuel kontrol ile daha güvenli platform tespiti
const getAdMobRewardedAdUnitId = () => {
  if (isWebPlatform) {
    return null;
  }

  if (__DEV__ && TestIds?.REWARDED) {
    return TestIds.REWARDED;
  }
  
  // Platform.OS değerini manuel kontrol et
  const platform = Platform.OS;
  
  if (platform === 'ios') {
    return 'ca-app-pub-2891866243585939/7165729408'; // iOS Rewarded Ad Unit ID (gerçek)
  } else if (platform === 'android') {
    return 'ca-app-pub-2891866243585939/3554060211'; // Android Rewarded Ad Unit ID (gerçek)
  } else {
    // Fallback: Android
    return 'ca-app-pub-2891866243585939/3554060211'; // Android fallback
  }
};

const ADMOB_REWARDED_AD_UNIT_ID = getAdMobRewardedAdUnitId();

if (!isWebPlatform) {
  console.log('[AdMob] Platform:', Platform.OS);
  console.log('[AdMob] Ad Unit ID:', ADMOB_REWARDED_AD_UNIT_ID);
  console.log('[AdMob] Expected Android ID:', 'ca-app-pub-2891866243585939/3554060211');
}
console.log('[AdMob] Expected iOS ID:', 'ca-app-pub-2891866243585939/7165729408');

const CATEGORY_ORDER = [
  'all',
  'Türkçe Pop',
  'Nostalji',
  'Yabancı',
  'Romantik ve Slow',
  'Türkü',
  'Arabesk',
  'Karadeniz',
  'Klasik',
  'Haber',
  'Spor',
];

const generateRandomNickname = () => {
  const randomNumber = Math.floor(100000 + Math.random() * 900000);
  return `Kullanıcı-${randomNumber}`;
};

const normalizeCategory = (category) => {
  if (!category) {
    return null;
  }
  const trimmed = category.trim();
  if (trimmed === 'Pop') {
    return 'Türkçe Pop';
  }
  if (trimmed === 'Türkçe Pop') {
    return trimmed;
  }
  if (['Romantik', 'Slow', 'Romantik ve Slow'].includes(trimmed)) {
    return 'Romantik ve Slow';
  }
  return trimmed;
};

const getCategoryLabel = (category) => {
  if (category === 'all') {
    return 'Tümü';
  }
  return category;
};

const LISTENING_REWARD_DURATION_MS = 10 * 60 * 1000; // 10 dakika
const PISTI_GAME = {
  id: 'pisti',
  name: 'Pişti',
  description: 'Klasik kart oyunu - 2-4 oyuncu',
};
const UNO_GAME = {
  id: 'uno',
  name: 'UNO',
  description: 'UNO kart oyunu - 2-6 oyuncu',
};
const BATAK_GAME = {
  id: 'batak',
  name: 'Batak',
  description: 'Koz maça - 4 oyuncu',
};

const darkTheme = {
  ...MD3DarkTheme,
  colors: {
    ...MD3DarkTheme.colors,
    primary: '#E94560',          // vivid crimson
    secondary: '#00C2A8',        // teal accent
    background: '#0F1115',       // deep charcoal
    surface: '#151A22',          // dark slate
    surfaceVariant: '#222835',   // muted slate
    error: '#FF6B6B',
    onPrimary: '#0B0E14',
    onSecondary: '#0B0E14',
    onBackground: '#E6EAF2',
    onSurface: '#E6EAF2',
    onError: '#0B0E14',
  },
};

// Railway kullanmak için (önerilen - tüm sorunları çözer):
const API_BASE_URL = `${RAILWAY_URL}/api`;

// Veya localhost kullanmak için (development):
// const getApiBaseUrl = () => {
//   if (__DEV__) {
//     if (Platform.OS === 'android') {
//       return 'http://10.0.2.2:3000/api'; // Android emülatör
//     }
//     return 'http://localhost:3000/api'; // iOS simulator
//   }
//   return `${RAILWAY_URL}/api`; // Production
// };
// const API_BASE_URL = getApiBaseUrl();

import ChatRoom from './components/ChatRoom';
import RoomList from './components/RoomList';
import ProfileModal from './components/ProfileModal';
import GameRoomList from './components/GameRoomList';
import GameCarousel from './components/GameCarousel';
// Global ses yönetimi - tüm sesleri tek bir yerde tut
// React Native'de global objesini kullanarak paylaşılan değişken oluşturuyoruz
if (typeof global.globalSoundInstance === 'undefined') {
  global.globalSoundInstance = null;
}
if (typeof global.isPlayingAudio === 'undefined') {
  global.isPlayingAudio = false; // Ses çalma işlemi devam ediyor mu?
}

const MATERIAL_COMMUNITY_ICONS_TTF = require('@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/MaterialCommunityIcons.ttf');

export default function App() {
  // Web debug
  if (isWebPlatform) {
    console.log('[Web Debug] App component rendering...');
  }
  
  // Mobil cihaz kontrolü - daha iyi responsive breakpoint yönetimi
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  useEffect(() => {
    if (!isWebPlatform || typeof window === 'undefined') return;
    
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 640); // Mobile: <= 640px
      setIsTablet(width > 640 && width <= 1024); // Tablet: 641-1024px
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    window.addEventListener('orientationchange', checkDevice);
    return () => {
      window.removeEventListener('resize', checkDevice);
      window.removeEventListener('orientationchange', checkDevice);
    };
  }, []);
  
  const [fontsLoaded, fontsError] = useFonts({
    MaterialCommunityIcons: MATERIAL_COMMUNITY_ICONS_TTF,
  });
  const [radios, setRadios] = useState([]);
  const [filteredRadios, setFilteredRadios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sound, setSound] = useState(null);
  const [playingRadio, setPlayingRadio] = useState(null);
  const [categories, setCategories] = useState(['all']);
  const [showChat, setShowChat] = useState(false);
  const [showRoomList, setShowRoomList] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [socialRadioId, setSocialRadioId] = useState(null);
  const [showPistiRoomList, setShowPistiRoomList] = useState(false);
  const [showPistiChat, setShowPistiChat] = useState(false);
  const [selectedPistiRoom, setSelectedPistiRoom] = useState(null);
  const [pistiState, setPistiState] = useState(null);
  const [showUnoRoomList, setShowUnoRoomList] = useState(false);
  const [showUnoChat, setShowUnoChat] = useState(false);
  const [selectedUnoRoom, setSelectedUnoRoom] = useState(null);
  const [unoState, setUnoState] = useState(null);
  const [showBatakRoomList, setShowBatakRoomList] = useState(false);
  const [showBatakChat, setShowBatakChat] = useState(false);
  const [selectedBatakRoom, setSelectedBatakRoom] = useState(null);
  const [batakState, setBatakState] = useState(null);
  const pistiRadioRef = useRef(null); // Pişti odasına girildiğinde çalacak radyo
  const batakRadioRef = useRef(null);
  const unoRadioRef = useRef(null);
  const gameCarouselStateRef = useRef({ activeIndex: 0 });
  const defaultProfile = useMemo(() => ({
    nickname: generateRandomNickname(),
    avatar: { ...DEFAULT_AVATAR_OPTION },
    nicknameLocked: false,
  }), []);
  const [profile, setProfile] = useState(defaultProfile);
  const [profileDraft, setProfileDraft] = useState(defaultProfile);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAboutPage, setShowAboutPage] = useState(false);
  const [showContactPage, setShowContactPage] = useState(false);
  const [showPrivacyPolicyPage, setShowPrivacyPolicyPage] = useState(false);
  const [mainMenuVisible, setMainMenuVisible] = useState(false);
  const menuContainerRef = useRef(null);
  // Top listeler: Yerli/Yabancı pop öne çıkarma (radyo kartları üstte kalır)
  const topTurkPop = useMemo(() => {
    if (!Array.isArray(radios) || radios.length === 0) return [];
    return radios
      .filter(r => {
        const cat = (r.normalizedCategory || r.category || '').toLowerCase();
        return cat.includes('türk') || cat.includes('turk');
      })
      .slice(0, 6);
  }, [radios]);

  const topYabanciPop = useMemo(() => {
    if (!Array.isArray(radios) || radios.length === 0) return [];
    return radios
      .filter(r => {
        const cat = (r.normalizedCategory || r.category || '').toLowerCase();
        return cat.includes('yabancı') || cat.includes('yabanci') || cat.includes('foreign');
      })
      .slice(0, 6);
  }, [radios]);

  // Web için URL routing
  useEffect(() => {
    if (!isWebPlatform || typeof window === 'undefined') return;

    // URL'den sayfa durumunu kontrol et
    const path = window.location.pathname;
    if (path === '/hakkimizda' || path === '/about') {
      setShowAboutPage(true);
    } else if (path === '/iletisim' || path === '/contact') {
      setShowContactPage(true);
    } else if (path === '/gizlilik-politikasi' || path === '/privacy-policy') {
      setShowPrivacyPolicyPage(true);
    }

    // Browser geri/ileri butonları için
    const handlePopState = () => {
      const path = window.location.pathname;
      setShowAboutPage(path === '/hakkimizda' || path === '/about');
      setShowContactPage(path === '/iletisim' || path === '/contact');
      setShowPrivacyPolicyPage(path === '/gizlilik-politikasi' || path === '/privacy-policy');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isWebPlatform]);

  // Sayfa açıldığında URL'i güncelle
  const openAboutPage = useCallback(() => {
    setShowAboutPage(true);
    if (isWebPlatform && typeof window !== 'undefined') {
      window.history.pushState({ page: 'about' }, '', '/hakkimizda');
      document.title = 'Hakkımızda - Şamata Play';
    }
  }, [isWebPlatform]);

  const openContactPage = useCallback(() => {
    setShowContactPage(true);
    if (isWebPlatform && typeof window !== 'undefined') {
      window.history.pushState({ page: 'contact' }, '', '/iletisim');
      document.title = 'İletişim - Şamata Play';
    }
  }, [isWebPlatform]);

  const openPrivacyPolicyPage = useCallback(() => {
    setShowPrivacyPolicyPage(true);
    if (isWebPlatform && typeof window !== 'undefined') {
      window.history.pushState({ page: 'privacy' }, '', '/gizlilik-politikasi');
      document.title = 'Gizlilik Politikası - Şamata Play';
    }
  }, [isWebPlatform]);

  // Sayfa kapatıldığında URL'i güncelle
  const closeAboutPage = useCallback(() => {
    setShowAboutPage(false);
    if (isWebPlatform && typeof window !== 'undefined') {
      window.history.pushState({ page: 'home' }, '', '/');
      document.title = 'Şamata Play - Canlı Radyo Dinle | Pişti, UNO, Batak Oyna';
    }
  }, [isWebPlatform]);

  const closeContactPage = useCallback(() => {
    setShowContactPage(false);
    if (isWebPlatform && typeof window !== 'undefined') {
      window.history.pushState({ page: 'home' }, '', '/');
      document.title = 'Şamata Play - Canlı Radyo Dinle | Pişti, UNO, Batak Oyna';
    }
  }, [isWebPlatform]);

  const closePrivacyPolicyPage = useCallback(() => {
    setShowPrivacyPolicyPage(false);
    if (isWebPlatform && typeof window !== 'undefined') {
      window.history.pushState({ page: 'home' }, '', '/');
      document.title = 'Şamata Play - Canlı Radyo Dinle | Pişti, UNO, Batak Oyna';
    }
  }, [isWebPlatform]);
  const [userName, setUserName] = useState(defaultProfile.nickname);
  const [userAvatar, setUserAvatar] = useState(normalizeAvatar(DEFAULT_AVATAR_OPTION));
  const [favorites, setFavorites] = useState([]);
  const [recentRadios, setRecentRadios] = useState([]);
  const [listenerCounts, setListenerCounts] = useState({}); // radioId -> count
  const [tokens, setTokens] = useState(0); // Sosyal Jeton sayısı
  const [isRewardedAdLoading, setIsRewardedAdLoading] = useState(false);
  const pistiRadio = useMemo(() => ({
    _id: `game-${PISTI_GAME.id}`,
    name: PISTI_GAME.name,
    description: PISTI_GAME.description,
    streamUrl: null,
  }), []);
const unoRadio = useMemo(() => ({
  _id: `game-${UNO_GAME.id}`,
  name: UNO_GAME.name,
  description: UNO_GAME.description,
  streamUrl: null,
}), []);
  const batakRadio = useMemo(() => ({
    _id: `game-${BATAK_GAME.id}`,
    name: BATAK_GAME.name,
    description: BATAK_GAME.description,
    streamUrl: null,
  }), []);
  const rewardedAdRef = useRef(null);
  const shouldShowRewardedOnLoadRef = useRef(false);
  const playRequestIdRef = useRef(0);
  const tokensRef = useRef(0);
  const listeningAccumulatedRef = useRef(0);
  const listeningLastActiveRef = useRef(null);
  const listeningTimerRef = useRef(null);
  const listeningRewardGivenRef = useRef(false);
  const appStateRef = useRef(AppState.currentState);
  const isListeningRef = useRef(false);
  const listenerSocketRef = useRef(null); // Radyo dinleyici sayısı için Socket.IO bağlantısı
  const isStartingPlaybackRef = useRef(false); // eşzamanlı oynatma isteklerini kilitle
  const lastGameRewardRoundRef = useRef(null);
  const lastMysteryRewardRef = useRef(null);
  const hilltopAdsIntervalRef = useRef(null); // HilltopAds periyodik reklam gösterimi için

  useEffect(() => {
    MaterialCommunityIcons.loadFont().catch((error) => {
      if (__DEV__) {
        console.warn('MaterialCommunityIcons font load failed:', error);
      }
    });
  }, []);

  // AdMob (react-native-google-mobile-ads) başlangıç
  useEffect(() => {
    if (isWebPlatform || !mobileAdsModule) {
      return;
    }
    try {
      // Varsayılan config ile AdMob SDK'yı başlat
      // Not: mobileAdsModule hem fonksiyon hem de statik üyeler içeren bir obje
      const mobileAdsInstance = typeof mobileAdsModule === 'function'
        ? mobileAdsModule()
        : mobileAdsModule.default
          ? mobileAdsModule.default()
          : null;

      if (mobileAdsInstance && typeof mobileAdsInstance.initialize === 'function') {
        mobileAdsInstance.initialize().then(() => {
          console.log('[AdMob] Initialized on mobile');
        }).catch((error) => {
          console.warn('[AdMob] initialize() error:', error);
        });
      } else {
        console.warn('[AdMob] mobileAds.initialize bulunamadı, ancak RewardedAd yine de kullanılacak');
      }
    } catch (error) {
      console.warn('[AdMob] Initialization failed:', error);
    }
  }, []);

  // OTA güncellemelerini kontrol et
  useEffect(() => {
    async function onFetchUpdateAsync() {
      try {
        if (!isWebPlatform) {
          console.log('[OTA] Updates.isEnabled:', Updates.isEnabled);
          console.log('[OTA] Updates.updateId:', Updates.updateId);
          console.log('[OTA] Updates.manifest:', Updates.manifest ? 'exists' : 'null');
          console.log('[OTA] __DEV__:', __DEV__);
          
          if (!__DEV__ && Updates.isEnabled) {
            console.log('[OTA] Checking for updates...');
            const update = await Updates.checkForUpdateAsync();
            console.log('[OTA] Update check result:', {
              isAvailable: update.isAvailable,
              manifest: update.manifest ? 'exists' : 'null',
            });
            
            if (update.isAvailable) {
              console.log('[OTA] Update available, fetching...');
              const fetchResult = await Updates.fetchUpdateAsync();
              console.log('[OTA] Update fetched successfully:', {
                isNew: fetchResult.isNew,
                manifest: fetchResult.manifest ? 'exists' : 'null',
              });
              
              // Güncelleme yüklendiyse reload et
              if (fetchResult.isNew) {
                console.log('[OTA] New update loaded, reloading app...');
                // Kısa bir gecikme ile reload et (kullanıcıya bilgi verilebilir)
                setTimeout(() => {
                  Updates.reloadAsync().catch(err => {
                    console.error('[OTA] Reload hatası:', err);
                    // Reload başarısız olursa uygulamayı yeniden başlatmayı dene
                    if (Platform.OS === 'android') {
                      // Android için alternatif reload yöntemi
                      Updates.reloadAsync();
                    }
                  });
                }, 500);
              } else {
                console.log('[OTA] Update already loaded, no reload needed');
              }
            } else {
              console.log('[OTA] No update available');
            }
          } else {
            console.log('[OTA] Updates disabled or in dev mode');
          }
        }
      } catch (error) {
        console.error('[OTA] Güncelleme kontrolü hatası:', error);
        console.error('[OTA] Error details:', error.message, error.stack);
      }
    }
    
    // Kısa bir gecikme ile kontrol et (uygulama tamamen yüklensin)
    const timeout = setTimeout(() => {
      onFetchUpdateAsync();
    }, 1000);
    
    return () => clearTimeout(timeout);
  }, []);

  // AppState aktif olduğunda güncellemeleri kontrol et
  useEffect(() => {
    const subscription = AppState.addEventListener('change', async (nextAppState) => {
      if (nextAppState === 'active' && !__DEV__ && !isWebPlatform && Updates.isEnabled) {
        try {
          console.log('[OTA] AppState changed to active, checking for updates...');
          const update = await Updates.checkForUpdateAsync();
          if (update.isAvailable) {
            console.log('[OTA] Update available on app resume, fetching...');
            const fetchResult = await Updates.fetchUpdateAsync();
            console.log('[OTA] Update fetched on resume:', {
              isNew: fetchResult.isNew,
              manifest: fetchResult.manifest ? 'exists' : 'null',
            });
            
            if (fetchResult.isNew) {
              console.log('[OTA] New update loaded on resume, reloading app...');
              setTimeout(() => {
                Updates.reloadAsync().catch(err => {
                  console.error('[OTA] Reload hatası (resume):', err);
                });
              }, 500);
            }
          } else {
            console.log('[OTA] No update available on app resume');
          }
        } catch (error) {
          console.error('[OTA] AppState change güncelleme kontrolü hatası:', error);
        }
      }
    });
    return () => subscription.remove();
  }, []);

  useEffect(() => {
    tokensRef.current = tokens;
  }, [tokens]);

  // Jeton yönetimi fonksiyonları (grantListeningReward'tan önce tanımlanmalı)
  const saveTokens = useCallback(async (newTokens) => {
    try {
      await AsyncStorage.setItem(TOKENS_STORAGE_KEY, newTokens.toString());
      setTokens(newTokens);
    } catch (error) {
      console.error('Jetonlar kaydedilemedi:', error);
    }
  }, []);

  const clearListeningTimer = useCallback(() => {
    if (listeningTimerRef.current) {
      clearTimeout(listeningTimerRef.current);
      listeningTimerRef.current = null;
    }
  }, []);

  const pauseListeningTracking = useCallback(() => {
    if (listeningLastActiveRef.current) {
      listeningAccumulatedRef.current += Date.now() - listeningLastActiveRef.current;
      listeningLastActiveRef.current = null;
    }
    clearListeningTimer();
  }, [clearListeningTimer]);

  const grantListeningReward = useCallback(async () => {
    if (listeningRewardGivenRef.current) {
      return;
    }
    listeningRewardGivenRef.current = true;
    clearListeningTimer();
    listeningLastActiveRef.current = null;
    listeningAccumulatedRef.current = LISTENING_REWARD_DURATION_MS;
    const newTokens = tokensRef.current + 1;
    tokensRef.current = newTokens;
    await saveTokens(newTokens);
    try {
      Alert.alert('Tebrikler!', '10 dakika dinlediğin için +1 Sosyal Enerji kazandın.');
    } catch (e) {
      // Alert başarısız olabilir; sessizce yoksay
    }
  }, [clearListeningTimer, saveTokens]);

  const scheduleListeningReward = useCallback(() => {
    clearListeningTimer();
    if (listeningRewardGivenRef.current || !isListeningRef.current || appStateRef.current !== 'active') {
      listeningLastActiveRef.current = null;
      return;
    }
    const remaining = LISTENING_REWARD_DURATION_MS - listeningAccumulatedRef.current;
    if (remaining <= 0) {
      grantListeningReward();
      return;
    }
    listeningLastActiveRef.current = Date.now();
    listeningTimerRef.current = setTimeout(() => {
      listeningAccumulatedRef.current = LISTENING_REWARD_DURATION_MS;
      grantListeningReward();
    }, remaining);
  }, [clearListeningTimer, grantListeningReward]);

  const handleAppStateChange = useCallback((nextAppState) => {
    const previousState = appStateRef.current;
    appStateRef.current = nextAppState;
    if (nextAppState === 'active') {
      if (isListeningRef.current && !listeningRewardGivenRef.current) {
        scheduleListeningReward();
      }
    } else if (previousState === 'active') {
      if (isListeningRef.current && !listeningRewardGivenRef.current) {
        pauseListeningTracking();
      }
    }
  }, [pauseListeningTracking, scheduleListeningReward]);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', handleAppStateChange);
    return () => {
      subscription.remove();
    };
  }, [handleAppStateChange]);

  useEffect(() => () => clearListeningTimer(), [clearListeningTimer]);

  useEffect(() => {
    let isMounted = true;
    const fetchGameSummaries = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/games`,
          { timeout: 8000 }
        );
        if (isMounted && response?.data?.games) {
          const pistiGame = response.data.games.find(g => g.id === PISTI_GAME.id);
          if (pistiGame) {
            setPistiState(pistiGame);
          }
          const unoGame = response.data.games.find(g => g.id === UNO_GAME.id);
          if (unoGame) {
            setUnoState(unoGame);
          }
          const batakGame = response.data.games.find(g => g.id === BATAK_GAME.id);
          if (batakGame) {
            setBatakState(batakGame);
          }
          const okeyGame = response.data.games.find(g => g.id === OKEY_GAME.id);
          if (okeyGame) {
            setOkeyState(okeyGame);
          }
        }
      } catch (error) {
        if (__DEV__) {
          console.warn('Oyun durumları alınamadı:', error?.message || error);
        }
      }
    };

    fetchGameSummaries();
    const interval = setInterval(fetchGameSummaries, 15000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Uygulama genel ses modu ayarı (expo-av)
  // İSTEK: Sadece ANDROID'de radyo arka planda çalsın
  useEffect(() => {
    // Web ve iOS'ta bu özelliği açmıyoruz
    if (Platform.OS !== 'android') {
      return;
    }
    (async () => {
      try {
        await Audio.setAudioModeAsync({
          staysActiveInBackground: true,
          shouldDuckAndroid: true,
          playThroughEarpieceAndroid: false,
          interruptionModeAndroid: Audio.INTERRUPTION_MODE_ANDROID_DO_NOT_MIX,
        });
      } catch (e) {
        // sessizce geç
      }
    })();
  }, []);

  // useKeepAwake web'de çalışmaz ama hook kuralları gereği her zaman çağrılmalı
  useKeepAwake();

  useEffect(() => {
    if (isWebPlatform) {
      return;
    }
    (async () => {
      try {
        await NavigationBar.setVisibilityAsync('immersive');
        await NavigationBar.setBehaviorAsync('overlay-swipe');
        await NavigationBar.setBackgroundColorAsync('#00000000');
        await NavigationBar.setButtonStyleAsync('light');
      } catch (e) {
        // sessiz geç
      }
    })();
  }, [isWebPlatform]);

  // SEO: Web için initial setup
  // SEO Structured Data (JSON-LD) ekle - Web platformu için
  useEffect(() => {
    if (!isWebPlatform || typeof document === 'undefined') {
      return;
    }

    // Structured Data (JSON-LD) için script tag'leri oluştur
    const structuredDataScripts = [
      {
        id: 'schema-org-webapp',
        json: {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Şamata Play",
          "url": "https://www.samataplay.com",
          "description": "Türkiye'nin en popüler canlı radyo dinleme platformu! Sezen Aksu, Tarkan, Ajda Pekkan, Edis, Mert Demir ve daha fazla Türk sanatçıları hakkında bilgi alın. Canlı radyo dinleyin, pişti oyna, UNO oyna, batak oyna. Chat ve sohbet odalarında yeni arkadaşlar tanıyın.",
          "applicationCategory": "EntertainmentApplication",
          "operatingSystem": "Web, Android, iOS",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "TRY"
          },
          "featureList": [
            "Canlı radyo dinleme",
            "Türk sanatçıları bilgileri",
            "Türk pop müziği",
            "Pişti oyunu",
            "UNO oyunu",
            "Batak oyunu",
            "Chat ve sohbet",
            "Bilgi yarışması",
            "Arkadaşlık ve sosyalleşme",
            "Online kart oyunları",
            "Kelime avı",
            "Kategori savaşı"
          ],
          "keywords": "Türk sanatçıları, Sezen Aksu, Tarkan, Ajda Pekkan, Edis, Mert Demir, Türk pop müziği, canlı radyo, pişti, UNO, batak"
        }
      },
      {
        id: 'schema-org-website',
        json: {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Şamata Play",
          "url": "https://www.samataplay.com",
          "description": "Türkiye'nin en popüler canlı radyo platformu! Türk sanatçıları hakkında bilgi, canlı radyo dinle, pişti oyna, UNO oyna, batak oyna. Chat ve sohbet et, yeni arkadaşlar tanı.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://www.samataplay.com/?search={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }
      },
      {
        id: 'schema-org-organization',
        json: {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Şamata Play",
          "url": "https://www.samataplay.com",
          "logo": "https://www.samataplay.com/assets/samata512x512.png",
          "sameAs": [
            "https://twitter.com/samataplay"
          ]
        }
      }
    ];

    // Mevcut structured data script'lerini kaldır
    structuredDataScripts.forEach(({ id }) => {
      const existingScript = document.getElementById(id);
      if (existingScript) {
        existingScript.remove();
      }
    });

    // Yeni structured data script'lerini ekle
    structuredDataScripts.forEach(({ id, json }) => {
      const script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(json);
      document.head.appendChild(script);
    });

    // Cleanup function
    return () => {
      structuredDataScripts.forEach(({ id }) => {
        const script = document.getElementById(id);
        if (script) {
          script.remove();
        }
      });
    };
  }, []);

  useEffect(() => {
    if (!isWebPlatform) {
      return; // Web değilse hiçbir şey yapma
    }
    
    // Google AdSense script'ini ekle (webRewardedAd.js'deki ensureAdsenseScript kullanılıyor)
    // Burada sadece ensureAdsenseInitialized çağrılıyor, script yükleme webRewardedAd.js'de yapılıyor
    ensureAdsenseInitialized();
    
    // HilltopAds script - AdSense onayı alana kadar kullanılacak
    // Oyun odalarında (ChatRoom) reklam gösterilmez
    const isInGameRoom = showChat || showPistiChat || showUnoChat || showBatakChat;
    
    if (!isInGameRoom) {
      const loadHilltopAdsScript = () => {
        if (typeof document === 'undefined') return;
        
        // Mevcut script'i kontrol et
        const existingScript = document.querySelector('script[data-hilltopads="true"]');
        if (existingScript) {
          // Script zaten yüklü, sadece reklamı tetikle (eğer API varsa)
          // HilltopAds'in refresh mekanizması varsa burada çağrılabilir
          if (typeof window !== 'undefined' && window.pgjs && typeof window.pgjs.refresh === 'function') {
            window.pgjs.refresh();
          }
          return;
        }
        
        // Yeni script yükle
        (function(pgjs) {
          var d = document,
              s = d.createElement('script'),
              l = d.scripts[d.scripts.length - 1];
          s.settings = pgjs || {};
          s.src = "//ornery-possible.com/b.XaVwsFdtGMlE0IY/Wecd/WegmZ9Gu/ZaUZlskuPhTwYp3vMvTvYe3ROrD/IXtHN_j/c-xFNmjuci4SM/wQ";
          s.async = true;
          s.referrerPolicy = 'no-referrer-when-downgrade';
          s.setAttribute('data-hilltopads', 'true');
          l.parentNode.insertBefore(s, l);
        })({});
        console.log('[Web] HilltopAds script added');
      };
      
      // İlk yükleme
      loadHilltopAdsScript();
      
      // Belirli aralıklarla reklamı yenile (60 saniye = 60000ms)
      // Süreyi ihtiyaca göre ayarlayabilirsiniz (30 saniye, 1 dakika, 2 dakika vb.)
      const AD_REFRESH_INTERVAL = 60000; // 60 saniye
      hilltopAdsIntervalRef.current = setInterval(() => {
        // Oyun odasında değilsek reklamı yenile
        const stillInGameRoom = showChat || showPistiChat || showUnoChat || showBatakChat;
        if (stillInGameRoom) {
          return; // Oyun odasındaysak reklamı yenileme
        }
        
        // Mevcut script'i kaldır ve yeniden yükle
        const existingScript = document.querySelector('script[data-hilltopads="true"]');
        if (existingScript && existingScript.parentNode) {
          existingScript.parentNode.removeChild(existingScript);
        }
        // Kısa bir gecikme ile yeniden yükle
        setTimeout(() => {
          loadHilltopAdsScript();
        }, 1000);
      }, AD_REFRESH_INTERVAL);
    } else {
      // Oyun odasındayız, reklamı kaldır
      const existingScript = document.querySelector('script[data-hilltopads="true"]');
      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
        console.log('[Web] HilltopAds script removed (in game room)');
      }
      // Interval'i temizle
      if (hilltopAdsIntervalRef.current) {
        clearInterval(hilltopAdsIntervalRef.current);
        hilltopAdsIntervalRef.current = null;
      }
    }
    
    // Cleanup function
    return () => {
      if (hilltopAdsIntervalRef.current) {
        clearInterval(hilltopAdsIntervalRef.current);
        hilltopAdsIntervalRef.current = null;
      }
    };
  }, [showChat, showPistiChat, showUnoChat, showBatakChat]); // Oyun odası durumuna göre reklamı kontrol et

  // SEO: Web için initial setup (ayrı useEffect)
  useEffect(() => {
    if (isWebPlatform) {
      // SEO: Document title ve meta tags güncelle
      document.title = 'Şamata - Canlı Radyo Dinle | Türk Sanatçıları | Chat, Oyunlar';
      
      // Meta description güncelle
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', 'Şamata ile Türkiye\'nin en popüler radyo istasyonlarını canlı dinleyin. Sezen Aksu, Tarkan, Ajda Pekkan, Edis ve daha fazla Türk sanatçıları hakkında bilgi alın. Türk pop müziği, arabesk dinleyin. Chat yapın, bilgi yarışması oynayın, kelime avı yapın ve müzik keyfi çıkarın. Online radyo, sohbet odaları, interaktif oyunlar ve daha fazlası.');
      
      // Canonical URL ekle
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', 'https://www.samataplay.com');
      
      // Structured Data (JSON-LD) ekle
      const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'Şamata',
        applicationCategory: 'MusicApplication',
        operatingSystem: 'Web, Android, iOS',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'TRY',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.5',
          ratingCount: '1000',
        },
        description: 'Türkiye\'nin en popüler canlı radyo platformu! Sezen Aksu, Tarkan, Ajda Pekkan, Edis, Mert Demir ve daha fazla Türk sanatçıları hakkında bilgi alın. Canlı radyo dinleyin, pişti oyna, UNO oyna, batak oyna. Chat yapın, bilgi yarışması oynayın, kelime avı yapın ve müzik keyfi çıkarın. Türk pop müziği, arabesk, Türkçe müzik dinleyin.',
        featureList: [
          'Canlı Radyo Dinleme',
          'Chat ve Sohbet Odaları',
          'Bilgi Yarışması',
          'Kelime Avı Oyunu',
          'Kategori Savaşı Oyunu',
          'Gizemli Kelime Avı',
          'Popüler Radyolar',
          'Kategori Filtreleme',
        ],
        screenshot: 'https://www.samataplay.com/assets/samata512x512.png',
        url: 'https://www.samataplay.com',
      };
      
      // Eski structured data'yı kaldır
      const oldScripts = document.querySelectorAll('script[type="application/ld+json"]');
      oldScripts.forEach(script => script.remove());
      
      // Yeni structured data ekle
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);
      
      // Organization structured data
      const organizationData = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Şamata',
        url: 'https://www.samataplay.com',
        logo: 'https://www.samataplay.com/assets/samata512x512.png',
        description: 'Türkiye\'nin en popüler radyo dinleme platformu. Türk sanatçıları bilgileri, canlı radyo, Türk pop müziği, chat, oyunlar ve eğlence.',
        sameAs: [],
      };
      
      const orgScript = document.createElement('script');
      orgScript.type = 'application/ld+json';
      orgScript.text = JSON.stringify(organizationData);
      document.head.appendChild(orgScript);
      
      // RadioStation structured data (genel)
      const radioStationData = {
        '@context': 'https://schema.org',
        '@type': 'RadioStation',
        name: 'Şamata Radyo Platformu',
        description: 'Türkiye\'nin en popüler radyo istasyonlarını canlı dinleyin. Türk sanatçıları hakkında bilgi alın, Türk pop müziği dinleyin',
        url: 'https://www.samataplay.com',
      };
      
      const radioScript = document.createElement('script');
      radioScript.type = 'application/ld+json';
      radioScript.text = JSON.stringify(radioStationData);
      document.head.appendChild(radioScript);
    }
  }, [isWebPlatform]);
  
  // SEO: Oynatılan radyoya göre dinamik title güncelle
  useEffect(() => {
    if (isWebPlatform && playingRadio) {
      const radioName = playingRadio.name || 'Radyo';
      document.title = `${radioName} - Canlı Dinle | Şamata`;
      
      // Meta description'ı da güncelle
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', `${radioName} radyosunu canlı dinleyin. Şamata ile ${playingRadio.category || 'müzik'} kategorisindeki en popüler radyoları keşfedin. Türk sanatçıları hakkında bilgi alın, Türk pop müziği dinleyin. Chat yapın, oyunlar oynayın ve eğlenin.`);
      }
    } else if (isWebPlatform && !playingRadio) {
      // Radyo durdurulduğunda default title'a dön
      document.title = 'Şamata - Canlı Radyo Dinle | Türk Sanatçıları | Chat, Oyunlar';
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Şamata ile Türkiye\'nin en popüler radyo istasyonlarını canlı dinleyin. Sezen Aksu, Tarkan, Ajda Pekkan, Edis ve daha fazla Türk sanatçıları hakkında bilgi alın. Türk pop müziği, arabesk dinleyin. Chat yapın, bilgi yarışması oynayın, kelime avı yapın ve müzik keyfi çıkarın. Online radyo, sohbet odaları, interaktif oyunlar ve daha fazlası.');
      }
    }
  }, [isWebPlatform, playingRadio]);

  // saveTokens zaten yukarıda tanımlandı (grantListeningReward'tan önce)

  const addToken = useCallback(async () => {
    const newTokens = tokensRef.current + 1;
    tokensRef.current = newTokens;
    await saveTokens(newTokens);
  }, [saveTokens]);

  // Reklam yüklenemediğinde ücretsiz enerji verme (günlük limit yok)
  const checkAndGiveDailyFreeEnergy = useCallback(async () => {
    try {
      // Günlük limit yok, her seferinde enerji ver
      await addToken();
      Alert.alert(
        'Ücretsiz Enerji',
        'Reklam gösteredemedik ama sana bir enerji verdik. Sosyalleşmeye hazırsın!'
      );
      return true;
    } catch (error) {
      console.error('Ücretsiz enerji hatası:', error);
      return false;
    }
  }, [addToken]);

  const spendTokens = useCallback(async (amount) => {
    if (tokens >= amount) {
      const newTokens = tokens - amount;
      await saveTokens(newTokens);
      return true;
    }
    return false;
  }, [tokens, saveTokens]);

  const favoriteIds = useMemo(() => new Set(favorites.map(r => r._id)), [favorites]);
  const paperSettings = useMemo(
    () => ({
      icon: (props) => <MaterialCommunityIcons {...props} />,
    }),
    []
  );

  useEffect(() => {
    const loadStoredProfile = async () => {
      try {
        const stored = await AsyncStorage.getItem('@radyohub/profile');
        if (stored) {
          const parsed = JSON.parse(stored);
          const normalizedAvatar = normalizeAvatar(parsed?.avatar || DEFAULT_AVATAR_OPTION);
          let nicknameToUse = parsed?.nickname?.trim();
          let shouldPersistUpdate = false;

          if (!nicknameToUse || nicknameToUse === 'Kullanıcı') {
            nicknameToUse = generateRandomNickname();
            shouldPersistUpdate = true;
          }

          const withDefaults = {
            ...defaultProfile,
            ...parsed,
            nickname: nicknameToUse,
            avatar: normalizedAvatar,
            nicknameLocked:
              typeof parsed.nicknameLocked === 'boolean'
                ? parsed.nicknameLocked
                : nicknameToUse !== defaultProfile.nickname,
          };

          setProfile(withDefaults);
          setProfileDraft(withDefaults);
          setUserName(nicknameToUse);
          setUserAvatar(normalizedAvatar);

          if (shouldPersistUpdate) {
            const updatedProfile = {
              ...parsed,
              nickname: nicknameToUse,
              avatar: normalizedAvatar,
            };
            await AsyncStorage.setItem('@radyohub/profile', JSON.stringify(updatedProfile));
          }
        } else {
          const generatedNickname = generateRandomNickname();
          const normalizedAvatar = normalizeAvatar(DEFAULT_AVATAR_OPTION);
          const initialProfile = {
            nickname: generatedNickname,
            avatar: normalizedAvatar,
            nicknameLocked: false,
          };
          setProfile(initialProfile);
          setProfileDraft(initialProfile);
          setUserName(generatedNickname);
          setUserAvatar(normalizedAvatar);
          await AsyncStorage.setItem('@radyohub/profile', JSON.stringify(initialProfile));
        }
      } catch (error) {
        console.error('Profil yüklenemedi:', error);
      }
    };

    loadStoredProfile();
  }, [defaultProfile]);

  useEffect(() => {
    const loadSavedLists = async () => {
      try {
        const [favoritesJson, recentsJson, tokensJson] = await Promise.all([
          AsyncStorage.getItem(FAVORITES_STORAGE_KEY),
          AsyncStorage.getItem(RECENTS_STORAGE_KEY),
          AsyncStorage.getItem(TOKENS_STORAGE_KEY),
        ]);

        if (favoritesJson) {
          const parsedFavorites = JSON.parse(favoritesJson) || [];
          setFavorites(parsedFavorites);
        }
        if (recentsJson) {
          const parsedRecents = JSON.parse(recentsJson) || [];
          setRecentRadios(parsedRecents);
        }
        if (tokensJson) {
          setTokens(parseInt(tokensJson, 10) || 0);
        }
      } catch (error) {
        console.error('Favoriler veya son dinlenenler yüklenemedi:', error);
      }
    };

    loadSavedLists();
  }, []);

  useEffect(() => {
    loadRadios();
    
    // Aktif dinleyici sayılarını periyodik olarak güncelle (her 3 saniyede bir)
    const updateListenerCounts = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/radios`, {
          timeout: 5000, // 5 saniye timeout (periyodik güncelleme için)
        });
        const counts = {};
        (Array.isArray(response.data) ? response.data : []).forEach(radio => {
          const value =
            radio.activeListeners ??
            radio.listeners ??
            radio.listenerCount ??
            radio.listenersCount ??
            0;
          counts[radio._id] = typeof value === 'number' ? value : parseInt(value, 10) || 0;
        });
        setListenerCounts(counts);
      } catch (error) {
        // Network hatalarını sessizce yok say (kritik değil, periyodik güncelleme)
        // Sadece development modunda log at
        if (__DEV__ && error.code !== 'ECONNABORTED' && error.code !== 'ERR_NETWORK') {
          console.warn('Dinleyici sayısı güncellenemedi:', error.message);
        }
      }
    };
    
    // İlk güncellemeyi hemen yap
    updateListenerCounts();
    
    // Sonra periyodik olarak güncelle
    const interval = setInterval(updateListenerCounts, 3000); // 3 saniyede bir güncelle
    
    return () => {
      clearInterval(interval);
      // Socket.IO dinleyici bağlantısını temizle
      if (listenerSocketRef.current) {
        try {
          listenerSocketRef.current.disconnect();
          listenerSocketRef.current = null;
        } catch (error) {
          // Sessizce yok say
        }
      }
    };
  }, []);

  useEffect(() => {
    filterRadios();
  }, [selectedCategory, radios]);

  const showRewardedAd = useCallback(async () => {
    if (isWebPlatform) {
      if (!isAdsenseConfigured) {
        Alert.alert(
          'Reklam Hazır Değil',
          'Web reklam ayarları yapılandırılmadığı için ödüllü reklam gösteremiyoruz.'
        );
        return;
      }

      try {
        setIsRewardedAdLoading(true);
        await showWebRewardedInterstitial();
        await addToken();
        Alert.alert('Başarılı!', 'Reklamı izleyerek +1 Sosyal Enerji kazandınız.');
      } catch (error) {
        console.error('Web rewarded ad error:', error);
        Alert.alert(
          'Reklam Gösterilemedi',
          'Web reklamı açılamadı. Lütfen biraz sonra tekrar deneyin veya ücretsiz enerjiyi dene.'
        );
        await checkAndGiveDailyFreeEnergy();
      } finally {
        setIsRewardedAdLoading(false);
      }
      return;
    }

    const rewardedAd = rewardedAdRef.current;
    if (!rewardedAd || !ADMOB_REWARDED_AD_UNIT_ID) {
      Alert.alert(
        'Reklam Hazır Değil',
        'Reklam gösteredemedik ama sana bir enerji verdik. Sosyalleşmeye hazırsın!',
        [
          { text: 'İptal', style: 'cancel' },
          {
            text: 'Ücretsiz Enerji Al',
            onPress: async () => {
              await checkAndGiveDailyFreeEnergy();
            },
          },
        ]
      );
      return;
    }

    if (rewardedAd.loaded) {
      rewardedAd.show();
      return;
    }

    // Henüz yüklenmediyse, kullanıcı isteğiyle başlatılan yükleme için
    // yüklendikten sonra otomatik gösterilecek şekilde işaretle
    shouldShowRewardedOnLoadRef.current = true;
    setIsRewardedAdLoading(true);
    rewardedAd.load();
    const timeout = setTimeout(async () => {
      if (!rewardedAd.loaded) {
        setIsRewardedAdLoading(false);
        Alert.alert(
          'Reklam Yüklenemedi',
          'Reklam gösteredemedik ama sana bir enerji verdik. Sosyalleşmeye hazırsın!',
          [
            { text: 'İptal', style: 'cancel' },
            {
              text: 'Ücretsiz Enerji Al',
              onPress: async () => {
                await checkAndGiveDailyFreeEnergy();
              },
            },
          ]
        );
      }
    }, 5000);

    rewardedAd.onceLoadedCleanup = () => clearTimeout(timeout);
  }, [addToken, checkAndGiveDailyFreeEnergy, isAdsenseConfigured, isWebPlatform]);

  useEffect(() => {
    if (isWebPlatform || !ADMOB_REWARDED_AD_UNIT_ID || !RewardedAd) {
      return;
    }

    const rewardedAd = RewardedAd.createForAdRequest(ADMOB_REWARDED_AD_UNIT_ID, {
      requestNonPersonalizedAdsOnly: false,
    });

    rewardedAdRef.current = rewardedAd;

    const loadedListener = rewardedAd.addAdEventListener(
      RewardedAdEventType?.LOADED ?? 'rewarded_loaded',
      () => {
        setIsRewardedAdLoading(false);
        if (typeof rewardedAd.onceLoadedCleanup === 'function') {
          rewardedAd.onceLoadedCleanup();
          rewardedAd.onceLoadedCleanup = null;
        }
        // Eğer kullanıcı yükleme sırasında reklam istemişse, şimdi göster
        if (shouldShowRewardedOnLoadRef.current) {
          shouldShowRewardedOnLoadRef.current = false;
          try {
            rewardedAd.show();
          } catch (error) {
            console.error('RewardedAd show() after load error:', error);
          }
        }
      }
    );

    const closedListener = rewardedAd.addAdEventListener(
      AdEventType?.CLOSED ?? 'closed',
      () => {
        setIsRewardedAdLoading(false);
        rewardedAd.load();
      }
    );

    const errorListener = rewardedAd.addAdEventListener(
      AdEventType?.ERROR ?? 'error',
      async (error) => {
        console.error('Rewarded Ad Error:', error);
        setIsRewardedAdLoading(false);

        const errorCode = error?.code || error?.message || '';
        const errorString = String(errorCode).toLowerCase();

        if (
          errorString.includes('no fill') ||
          errorString.includes('no_fill') ||
          errorString.includes('error_code_no_fill') ||
          errorString.includes('no ad available')
        ) {
          Alert.alert(
            'Reklam Gösterilemedi',
            'Reklam gösteredemedik ama sana bir enerji verdik. Sosyalleşmeye hazırsın!',
            [
              { text: 'İptal', style: 'cancel' },
              {
                text: 'Ücretsiz Enerji Al',
                onPress: async () => {
                  await checkAndGiveDailyFreeEnergy();
                },
              },
            ]
          );
        }
      }
    );

    const rewardListener = rewardedAd.addAdEventListener(
      RewardedAdEventType?.EARNED_REWARD ?? 'rewarded_earned_reward',
      async () => {
        await addToken();
        Alert.alert('Başarılı!', 'Reklamı izleyerek +1 Sosyal Enerji kazandınız.');
      }
    );

    rewardedAd.load();

    return () => {
      rewardedAdRef.current = null;
      loadedListener();
      closedListener();
      errorListener();
      rewardListener();
    };
  }, [
    addToken,
    checkAndGiveDailyFreeEnergy,
    isWebPlatform,
  ]);

  const handleOpenProfile = () => {
    setProfileDraft(profile);
    setShowProfileModal(true);
  };

  const handleCloseProfile = () => {
    setShowProfileModal(false);
    setProfileDraft(profile);
  };

  const handleSaveProfile = async () => {
    const trimmedName = profileDraft.nickname?.trim();
    if (!trimmedName) {
      return;
    }

    const nicknameChanged =
      trimmedName !== profile.nickname && trimmedName !== defaultProfile.nickname;
    const shouldLockNickname = profile.nicknameLocked || nicknameChanged;

  const normalizedAvatar = normalizeAvatar(profileDraft.avatar);

    const newProfile = {
      ...profile,
      nickname: profile.nicknameLocked ? profile.nickname : trimmedName,
    avatar: normalizedAvatar,
      nicknameLocked: shouldLockNickname,
    };

    try {
      await AsyncStorage.setItem('@radyohub/profile', JSON.stringify(newProfile));
      setProfile(newProfile);
      setProfileDraft(newProfile);
      setUserName(newProfile.nickname);
    setUserAvatar(normalizedAvatar);
      setShowProfileModal(false);
    } catch (error) {
      console.error('Profil kaydedilemedi:', error);
      Alert.alert('Hata', 'Profilinizi kaydederken bir sorun oluştu.');
    }
  };

  // Icon to emoji mapping (ProfileModal ile aynı)
  const iconToEmoji = {
    'headphones': '🎧',
    'microphone-variant': '🎤',
    'record-player': '💿',
    'radio': '📻',
    'album': '💿',
    'guitar-electric': '🎸',
    'drum': '🥁',
    'music-note': '🎵',
    'music-circle': '🎶',
    'music-clef-treble': '🎼',
    'music-rest-whole': '🎵',
    'disc': '💿',
    'equalizer': '🎚️',
    'metronome': '⏱️',
    'cassette': '📼',
    'boombox': '📻',
    'tune-vertical': '🎛️',
    'rocket-launch': '🚀',
    'star-four-points': '⭐',
    'fire': '🔥',
    'turntable': '💿',
    'lightning-bolt': '⚡',
    'waveform': '〰️',
    'music-box': '🎵',
    'play-circle': '▶️',
    'music-note-plus': '🎵',
    'music-note-off': '🎵',
    'music-note-eighth': '🎵',
    'music-note-sixteenth': '🎵',
    'music-note-half': '🎵',
    'music-note-quarter': '🎵',
    'music-note-whole': '🎵',
    'music-note-multiple': '🎵',
    'music-circle-outline': '🎶',
    'karate': '🥋',
    'emoticon-cool-outline': '😎',
    'gamepad-variant': '🎮',
    'controller-classic': '🎮',
    'virtual-reality': '🥽',
    'robot-excited-outline': '🤖',
    'rocket-launch-outline': '🚀',
    'piano': '🎹',
    'violin': '🎻',
  };

  const getEmojiForIcon = (iconName) => {
    return iconToEmoji[iconName] || '🎵';
  };

  const renderAvatarBadge = (
    avatarData,
    size = 40,
    extraStyle = {},
    extraLabelStyle = {},
    fallbackLabel = '🙂'
  ) => {
    const normalized = normalizeAvatar(avatarData);

    if (normalized.type === AVATAR_TYPES.ICON) {
      const emoji = getEmojiForIcon(normalized.icon);
      return (
        <View
          style={[
            {
              width: size,
              height: size,
              borderRadius: size / 2,
              backgroundColor: normalized.backgroundColor,
              justifyContent: 'center',
              alignItems: 'center',
            },
            extraStyle,
          ]}
        >
          <Text style={{ fontSize: size * 0.5 }}>{emoji}</Text>
        </View>
      );
    }

    return (
      <Avatar.Text
        size={size}
        label={normalized.value || fallbackLabel}
        style={[{ backgroundColor: '#FFFFFF' }, extraStyle]}
        labelStyle={extraLabelStyle}
      />
    );
  };

  const toggleFavorite = (radio) => {
    if (!radio?._id) {
      return;
    }
    const normalizedRadio = radios.find(r => r._id === radio._id) || radio;
    setFavorites(prev => {
      const exists = prev.some(item => item._id === normalizedRadio._id);
      const updated = exists
        ? prev.filter(item => item._id !== normalizedRadio._id)
        : [normalizedRadio, ...prev];
      AsyncStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(updated)).catch(err => {
        console.error('Favoriler kaydedilemedi:', err);
      });
      return updated;
    });
  };

  const updateRecentRadios = (radio) => {
    if (!radio?._id) {
      return;
    }
    const normalizedRadio = radios.find(r => r._id === radio._id) || radio;
    setRecentRadios(prev => {
      const filtered = prev.filter(item => item._id !== normalizedRadio._id);
      const updated = [normalizedRadio, ...filtered].slice(0, 10);
      AsyncStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(updated)).catch(err => {
        console.error('Son dinlenenler kaydedilemedi:', err);
      });
      return updated;
    });
  };

  const handleRadioSelect = async (radio) => {
    if (!radio?._id) {
      return;
    }

    const wasSameRadio = playingRadio === radio._id;

    try {
      await stopRadio();

      if (wasSameRadio) {
        return;
      }

      setPlayingRadio(radio._id);
      await playRadio(radio);
    } catch (error) {
      console.error('Radyo seçim işlemi hatası:', error);
      setPlayingRadio(null);
    }
  };

  const handleOpenChat = async (radio, event) => {
    if (!radio?._id) {
      return;
    }

    // Web'de sayfa yenilenmesini önle (sadece stopPropagation, preventDefault render'ı bozabilir)
    if (event && isWebPlatform) {
      if (event.stopPropagation) {
        event.stopPropagation();
      }
      // React Native Web'de nativeEvent üzerinden de erişilebilir
      if (event.nativeEvent && event.nativeEvent.stopPropagation) {
        event.nativeEvent.stopPropagation();
      }
    }
    
    // Önce tüm sesleri durdur ve dinleyici bağlantısını kapat
    try {
      await stopRadio();
    } catch (err) {
      console.error('Radyo durdurma hatası:', err);
    }

    // Ana radios array'inde radyoyu bul, bulunamazsa geçirilen radio objesini kullan
    const currentRadio =
      radios.find(r => r._id === radio._id) ||
      radios.find(r => r.name === radio.name) ||
      radio;

    // Sadece oda listesi sayfasına yönlendir: chat odasına otomatik girme, bot oda oluşturma yok
    if (isWebPlatform) {
      console.log('[Web] Opening room list for radio:', currentRadio.name, currentRadio._id);
    }
    setSocialRadioId(currentRadio._id);
    setSelectedRoom(null);
    setShowChat(false);
 
    // Oda listesini göster
    setShowRoomList(true);
    // Not: Birlikte dinle akışında otomatik oda girişi ve otomatik çalma yapılmaz
  };

  const loadRadios = async () => {
    try {
      setLoading(true);
      console.log('API URL:', API_BASE_URL);
      
      const response = await axios.get(`${API_BASE_URL}/radios`, {
        timeout: 30000, // 30 saniye timeout (Railway için daha uzun)
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      
      const radioList = Array.isArray(response.data) ? response.data : [];

      const enrichedRadios = radioList.map((radio) => {
        const normalizedCategoryValue = normalizeCategory(radio.category);
        return {
          ...radio,
          normalizedCategory: normalizedCategoryValue,
          displayCategory: normalizedCategoryValue || radio.category || 'Genel',
        };
      });

      setRadios(enrichedRadios);
      setFilteredRadios(enrichedRadios);
 
      // Aktif dinleyici sayılarını ayır - tüm radyolar için başlangıçta 0
      const counts = {};
      enrichedRadios.forEach(radio => {
        counts[radio._id] = radio.activeListeners !== undefined ? radio.activeListeners : 0;
      });
      setListenerCounts(Object.fromEntries(
        Object.entries(counts).map(([k, v]) => [k, typeof v === 'number' ? v : parseInt(v, 10) || 0])
      ));
 
      // Kategorileri çıkar
      const categoryPresence = new Set();
      enrichedRadios.forEach((radio) => {
        if (radio.normalizedCategory) {
          categoryPresence.add(radio.normalizedCategory);
        }
      });

      const orderedCategories = CATEGORY_ORDER.filter(
        (category) => category === 'all' || categoryPresence.has(category)
      );
      const additionalCategories = [...categoryPresence].filter(
        (category) => !CATEGORY_ORDER.includes(category)
      );
      setCategories([...orderedCategories, ...additionalCategories]);
    } catch (error) {
      console.error('Radyo yükleme hatası:', error);
      console.error('Hata detayı:', {
        code: error.code,
        message: error.message,
        response: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        } : null,
        request: error.request ? 'İstek gönderildi ama yanıt alınamadı' : 'İstek gönderilemedi',
        url: API_BASE_URL,
        railwayUrl: RAILWAY_URL
      });
      
      let errorMessage = 'Bir bağlantı sorunu yaşandı. Lütfen internet bağlantınızı kontrol edin, ardından uygulamayı kapatıp yeniden açın.';
      
      if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        errorMessage = 'Railway sunucusuna ulaşılamadı. Lütfen internet bağlantınızı kontrol edin ve uygulamayı kapatıp tekrar açın.';
      } else if (error.code === 'ETIMEDOUT' || error.message.includes('timeout')) {
        errorMessage = 'Bağlantı zaman aşımına uğradı. Lütfen internet bağlantınızı kontrol edip uygulamayı kapatıp tekrar açın.';
      } else if (error.response) {
        errorMessage = 'Sunucuda geçici bir sorun var. Lütfen biraz sonra tekrar deneyin.';
      } else if (error.request) {
        errorMessage = 'İstek tamamlanamadı. Lütfen internet bağlantınızı kontrol edin ve uygulamayı kapatıp tekrar açın.';
      }
      
      Alert.alert('Bağlantı Hatası', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filterRadios = () => {
    let filtered = [...radios];

    // Kategori filtresi
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((radio) => {
        const normalizedCategoryValue = radio.normalizedCategory || normalizeCategory(radio.category);
        if (!normalizedCategoryValue) {
          return false;
        }
        if (selectedCategory === 'Romantik ve Slow') {
          return normalizedCategoryValue === 'Romantik ve Slow';
        }
        return normalizedCategoryValue === selectedCategory;
      });
    }

    setFilteredRadios(filtered);
  };

  const playRadio = async (radio) => {
    // Eşzamanlı çalma isteklerini engelle
    if (isStartingPlaybackRef.current) {
      return;
    }
    isStartingPlaybackRef.current = true;
    let requestId;
    try {
      // ÖNCE TÜM SESLERİ DURDUR - stopRadio fonksiyonunu çağır
      console.log('Yeni yayın başlatılıyor, önceki tüm yayınlar durduruluyor...');
      await stopRadio();
      requestId = playRequestIdRef.current;

      // Ses tamamen durduktan sonra bekle
      await new Promise(resolve => setTimeout(resolve, 300));
      if (playRequestIdRef.current !== requestId) {
        console.log('Yeni yayın isteği iptal edildi (stopRadio sonrası).');
        isStartingPlaybackRef.current = false;
        return;
      }
      
      // Şimdi yeni radyoyu çalmaya başla
      global.isPlayingAudio = true;
      if (playRequestIdRef.current !== requestId) {
        console.log('Yeni yayın isteği iptal edildi (flag ayarı sonrası).');
        global.isPlayingAudio = false;
        isStartingPlaybackRef.current = false;
        return;
      }
      
      // Aynı radyo zaten çalıyorsa hiçbir şey yapma
      if (playingRadio === radio._id && global.globalSoundInstance) {
         try {
           const status = await global.globalSoundInstance.getStatusAsync();
           if (status.isLoaded && status.isPlaying) {
             global.isPlayingAudio = false;
             isStartingPlaybackRef.current = false;
             return;
           }
         } catch (e) {
           // Status alınamazsa devam et
         }
      }

      // Stream URL'ini temizle ve kontrol et
      let streamUrl = radio.streamUrl?.trim();
      if (!streamUrl) {
        throw new Error('Stream URL bulunamadı');
      }

      // Orijinal stream URL'ini sakla (HLS kontrolü için)
      const originalStreamUrl = streamUrl;
      
      // .m3u8 dosyaları için özel işlem (HLS formatı)
      // Orijinal URL'e göre kontrol et
      const isM3U8 = originalStreamUrl.includes('.m3u8') || originalStreamUrl.includes('playlist.m3u8');
      
      // Web'de HTTP stream URL'lerini backend proxy üzerinden serve et (Mixed Content sorununu önlemek için)
      // HTTP HLS stream'leri için de proxy kullan (Mixed Content hatası olmasın)
      // HTTPS HLS stream'leri için proxy kullanma - direkt HTTPS üzerinden çalışır
      if (isWebPlatform && streamUrl.startsWith('http://')) {
        // Backend proxy üzerinden stream'i serve et (HTTP stream'ler için - HLS ve non-HLS)
        const encodedUrl = encodeURIComponent(streamUrl);
        streamUrl = `${RAILWAY_URL}/api/stream-proxy?url=${encodedUrl}`;
        console.log('[Web] HTTP stream URL proxied through backend:', streamUrl);
      }

      // Her ihtimale karşı, yeni ses oluşturmadan önce mevcut instanceları kapat
      if (global.globalSoundInstance) {
        try {
          const status = await global.globalSoundInstance.getStatusAsync();
          if (status.isLoaded) {
            await global.globalSoundInstance.stopAsync();
            await global.globalSoundInstance.unloadAsync();
          }
        } catch (_) {}
        global.globalSoundInstance = null;
      }
      if (sound) {
        try {
          const status = await sound.getStatusAsync();
          if (status.isLoaded) {
            await sound.stopAsync();
            await sound.unloadAsync();
          }
        } catch (_) {}
        setSound(null);
      }
      
      // Yeni radyoyu çal
      let newSound;
      
      if (isWebPlatform) {
        // Web'de native HTML5 audio element kullan (daha güvenilir)
        try {
          // Önce expo-av ile dene
          const audioOptions = {
            shouldPlay: true,
            volume: 1.0,
            isLooping: false,
          };
          
          newSound = (await Audio.Sound.createAsync(
            { uri: streamUrl },
            audioOptions
          )).sound;
        } catch (expoError) {
          // expo-av başarısız olursa, native HTML5 audio element kullan
          console.warn('[Web] expo-av failed, trying native HTML5 audio:', expoError);
          
          if (typeof document !== 'undefined') {
            // Mevcut audio element'i kaldır
            const existingAudio = document.getElementById('web-radio-audio');
            if (existingAudio) {
              existingAudio.pause();
              existingAudio.src = '';
              existingAudio.remove();
            }
            
            // HLS stream'ler için hls.js kullan
            if (isM3U8) {
              // HLS stream'ler için hls.js yüklemeyi dene
              try {
                // hls.js script'ini dinamik olarak yükle
                if (!window.Hls) {
                  const hlsScript = document.createElement('script');
                  hlsScript.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
                  await new Promise((resolve, reject) => {
                    hlsScript.onload = resolve;
                    hlsScript.onerror = reject;
                    document.head.appendChild(hlsScript);
                  });
                }
                
                // Video element kullan (HLS için)
                const videoElement = document.createElement('video');
                videoElement.id = 'web-radio-audio';
                videoElement.crossOrigin = 'anonymous';
                videoElement.volume = 1.0;
                videoElement.style.display = 'none';
                document.body.appendChild(videoElement);
                
                if (window.Hls.isSupported()) {
                  const hls = new window.Hls({
                    // CORS ve network hatalarını handle et
                    xhrSetup: (xhr, url) => {
                      xhr.withCredentials = false;
                    },
                    // Debug için
                    debug: false,
                    // Network hatalarını retry et
                    maxBufferLength: 30,
                    maxMaxBufferLength: 600,
                    // Segment loading timeout
                    fragLoadingTimeOut: 20000,
                    manifestLoadingTimeOut: 10000,
                  });
                  
                  console.log('[Web] HLS.js loading source:', streamUrl);
                  hls.loadSource(streamUrl);
                  hls.attachMedia(videoElement);
                  
                  await new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => {
                      reject(new Error('HLS loading timeout'));
                    }, 30000);
                    
                    hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
                      console.log('[Web] HLS manifest parsed successfully');
                      clearTimeout(timeout);
                      videoElement.play().then(resolve).catch(reject);
                    });
                    
                    hls.on(window.Hls.Events.ERROR, (event, data) => {
                      console.error('[Web] HLS error:', data);
                      console.error('[Web] HLS error details:', {
                        type: data.type,
                        details: data.details,
                        fatal: data.fatal,
                        url: data.url,
                        loader: data.loader,
                        response: data.response,
                      });
                      if (data.fatal) {
                        clearTimeout(timeout);
                        // Network error için retry dene
                        if (data.type === window.Hls.ErrorTypes.NETWORK_ERROR) {
                          console.log('[Web] HLS network error, trying to recover...');
                          console.log('[Web] HLS error URL:', data.url);
                          hls.startLoad();
                        } else {
                          reject(new Error('HLS error: ' + data.type + ' - ' + (data.details || '')));
                        }
                      }
                    });
                    
                    // HLS.js'in nested playlist yükleme olaylarını logla
                    hls.on(window.Hls.Events.LEVEL_LOADING, (event, data) => {
                      console.log('[Web] HLS level loading:', data);
                    });
                    
                    hls.on(window.Hls.Events.LEVEL_LOADED, (event, data) => {
                      console.log('[Web] HLS level loaded:', data);
                    });
                  });
                  
                  // expo-av Sound wrapper oluştur
                  newSound = {
                    playAsync: async () => {
                      await videoElement.play();
                    },
                    pauseAsync: async () => {
                      videoElement.pause();
                    },
                    stopAsync: async () => {
                      videoElement.pause();
                      videoElement.currentTime = 0;
                    },
                    unloadAsync: async () => {
                      videoElement.pause();
                      if (hls) {
                        hls.destroy();
                      }
                      if (videoElement.parentNode) {
                        videoElement.parentNode.removeChild(videoElement);
                      }
                    },
                    setVolumeAsync: async (volume) => {
                      videoElement.volume = volume;
                    },
                    getStatusAsync: async () => {
                      return {
                        isLoaded: true,
                        isPlaying: !videoElement.paused,
                        positionMillis: videoElement.currentTime * 1000,
                        durationMillis: videoElement.duration ? videoElement.duration * 1000 : -1,
                      };
                    },
                    _videoElement: videoElement,
                    _hls: hls,
                  };
                  
                  console.log('[Web] Using hls.js for HLS stream');
                } else {
                  throw new Error('HLS is not supported in this browser');
                }
              } catch (hlsError) {
                console.error('[Web] HLS.js failed:', hlsError);
                throw hlsError;
              }
            } else {
              // Normal audio stream için HTML5 audio element kullan
              const audioElement = document.createElement('audio');
              audioElement.id = 'web-radio-audio';
              audioElement.src = streamUrl;
              audioElement.crossOrigin = 'anonymous';
              audioElement.preload = 'auto';
              audioElement.volume = 1.0;
              
              // Audio element'i document'e ekle
              document.body.appendChild(audioElement);
              
              // Play promise'ini handle et
              const playPromise = audioElement.play();
              if (playPromise !== undefined) {
                await playPromise;
              }
            
            // expo-av Sound wrapper oluştur (compatibility için)
            newSound = {
              playAsync: async () => {
                await audioElement.play();
              },
              pauseAsync: async () => {
                audioElement.pause();
              },
              stopAsync: async () => {
                audioElement.pause();
                audioElement.currentTime = 0;
              },
              unloadAsync: async () => {
                audioElement.pause();
                audioElement.src = '';
                if (audioElement.parentNode) {
                  audioElement.parentNode.removeChild(audioElement);
                }
              },
              setVolumeAsync: async (volume) => {
                audioElement.volume = volume;
              },
              getStatusAsync: async () => {
                return {
                  isLoaded: true,
                  isPlaying: !audioElement.paused,
                  positionMillis: audioElement.currentTime * 1000,
                  durationMillis: audioElement.duration ? audioElement.duration * 1000 : -1,
                };
              },
              _audioElement: audioElement, // Internal reference
            };
            
              // expo-av Sound wrapper oluştur (compatibility için)
              newSound = {
                playAsync: async () => {
                  await audioElement.play();
                },
                pauseAsync: async () => {
                  audioElement.pause();
                },
                stopAsync: async () => {
                  audioElement.pause();
                  audioElement.currentTime = 0;
                },
                unloadAsync: async () => {
                  audioElement.pause();
                  audioElement.src = '';
                  if (audioElement.parentNode) {
                    audioElement.parentNode.removeChild(audioElement);
                  }
                },
                setVolumeAsync: async (volume) => {
                  audioElement.volume = volume;
                },
                getStatusAsync: async () => {
                  return {
                    isLoaded: true,
                    isPlaying: !audioElement.paused,
                    positionMillis: audioElement.currentTime * 1000,
                    durationMillis: audioElement.duration ? audioElement.duration * 1000 : -1,
                  };
                },
                _audioElement: audioElement, // Internal reference
              };
              
              console.log('[Web] Using native HTML5 audio element for stream');
            }
          } else {
            throw new Error('Document is not available');
          }
        }
      } else {
        // Mobil'de expo-av kullan
        const audioOptions = {
          shouldPlay: true,
          volume: 1.0,
          isLooping: false,
        };
        
        newSound = (await Audio.Sound.createAsync(
          { uri: streamUrl },
          audioOptions
        )).sound;
      }
      if (playRequestIdRef.current !== requestId) {
        try {
          await newSound.stopAsync();
        } catch (e) {
          // ignore
        }
        try {
          await newSound.unloadAsync();
        } catch (e) {
          // ignore
        }
        isStartingPlaybackRef.current = false;
        return;
      }
      
      // Global ve local ses referanslarını güncelle
      global.globalSoundInstance = newSound;
      setSound(newSound);
      setPlayingRadio(radio._id);
      isListeningRef.current = true;
      scheduleListeningReward();
 
      // Ses çalma işlemi tamamlandı
      global.isPlayingAudio = false;
      console.log('Radyo başarıyla çalıyor:', radio.name);

      updateRecentRadios(radio);

      // Dinleme sayısını artır
      try {
        await axios.post(`${API_BASE_URL}/radios/${radio._id}/listen`);
      } catch (error) {
        // Sessizce yok say
      }

      // Socket.IO'ya bağlan ve dinleyici olarak kayıt ol
      try {
        // Önceki bağlantıyı kapat
        if (listenerSocketRef.current) {
          listenerSocketRef.current.disconnect();
          listenerSocketRef.current = null;
        }

        // Yeni Socket.IO bağlantısı oluştur
        const socket = io(RAILWAY_URL, {
          transports: ['websocket', 'polling'],
        });

        socket.on('connect', () => {
          // Radyo odasına katıl (chat yapmadan, sadece dinleyici olarak)
          socket.emit('join-room', {
            radioId: radio._id,
            userName: userName || 'Dinleyici',
            avatar: userAvatar,
          });
        });

        listenerSocketRef.current = socket;
      } catch (error) {
        // Socket.IO bağlantı hatası kritik değil, sessizce devam et
        if (__DEV__) {
          console.warn('Socket.IO dinleyici kaydı hatası:', error);
        }
      }
    } catch (error) {
      if (requestId && playRequestIdRef.current !== requestId) {
        return;
      }
      pauseListeningTracking();
      isListeningRef.current = false;
      console.error('Radyo çalma hatası:', error);
      console.error('Hata detayı:', {
        message: error.message,
        code: error.code,
        radio: radio.name,
        streamUrl: radio.streamUrl,
        isWeb: isWebPlatform
      });

      const radioName = radio?.name || 'Radyo';
      let errorMessage = `${radioName} şu anda çalınamıyor.\n\n`;
      
      // Web'de özel hata mesajı
      if (isWebPlatform && error.code === 9) {
        errorMessage += 'Bu radyo web tarayıcısında desteklenmeyen bir format kullanıyor olabilir.\n\n';
      }
      
      errorMessage += 'Lütfen başka bir radyo deneyin veya daha sonra tekrar deneyin.';

      Alert.alert('Radyo Çalma Hatası', errorMessage);
      setPlayingRadio(null);
      global.isPlayingAudio = false; // Hata durumunda da flag'i temizle
    } finally {
      isStartingPlaybackRef.current = false;
    }
  };

  const stopRadio = async () => {
    // Web'de native HTML5 audio element'i de durdur
    if (isWebPlatform && typeof document !== 'undefined') {
      const audioElement = document.getElementById('web-radio-audio');
      if (audioElement) {
        try {
          audioElement.pause();
          audioElement.src = '';
          audioElement.remove();
        } catch (e) {
          console.warn('[Web] Error stopping native audio element:', e);
        }
      }
    }
    playRequestIdRef.current += 1; // yeni oynatma talepleri için versiyon artır
    pauseListeningTracking();
    isListeningRef.current = false;
    // Flag'i false yap ki playRadio çağrıları beklemesin
    global.isPlayingAudio = false;
    
    // Web'de native HTML5 audio/video element'i ve HLS instance'ını da durdur
    if (isWebPlatform && typeof document !== 'undefined') {
      const mediaElement = document.getElementById('web-radio-audio');
      if (mediaElement) {
        try {
          // HLS instance'ını temizle
          if (mediaElement._hls) {
            mediaElement._hls.destroy();
            mediaElement._hls = null;
          }
          mediaElement.pause();
          mediaElement.src = '';
          if (mediaElement.parentNode) {
            mediaElement.parentNode.removeChild(mediaElement);
          }
        } catch (e) {
          console.warn('[Web] Error stopping native media element:', e);
        }
      }
    }
    
    // Socket.IO dinleyici bağlantısını kapat
    if (listenerSocketRef.current) {
      try {
        listenerSocketRef.current.disconnect();
        listenerSocketRef.current = null;
      } catch (error) {
        // Sessizce yok say
      }
    }
    
    // TÜM sesleri durdur - önce stop, sonra unload
    // Global ses instance'ını durdur
    if (global.globalSoundInstance) {
      try {
        const status = await global.globalSoundInstance.getStatusAsync();
        if (status.isLoaded) {
          await global.globalSoundInstance.stopAsync();
          await global.globalSoundInstance.unloadAsync();
        }
      } catch (e) {
        console.error('Global ses durdurma hatası:', e);
      }
      global.globalSoundInstance = null;
    }
    
    // Local ses state'ini durdur
    if (sound) {
      try {
        const status = await sound.getStatusAsync();
        if (status.isLoaded) {
          await sound.stopAsync();
          await sound.unloadAsync();
        }
      } catch (e) {
        console.error('Local ses durdurma hatası:', e);
      }
      setSound(null);
    }
    
    // Playing radio state'ini temizle
    setPlayingRadio(null);
    
  };

  const handleOpenPisti = useCallback(async (event, radio = null) => {
    // Web'de sayfa yenilenmesini önle (sadece stopPropagation)
    if (event && isWebPlatform && event.stopPropagation) {
      event.stopPropagation();
    }
    // Radyo bilgisini sakla (RoomList'ten geliyorsa)
    if (radio) {
      pistiRadioRef.current = radio;
    }
    try {
      await stopRadio();
    } catch (error) {
      console.error('Pişti kartı açılırken radyo durdurulamadı:', error);
    }
    setShowRoomList(false);
    setShowChat(false);
    setSelectedRoom(null);
    setSocialRadioId(null);
    setSelectedPistiRoom(null);
    setShowPistiChat(false);
    setShowPistiRoomList(true);
  }, [stopRadio]);

  const handleOpenUno = useCallback(async (event, radio = null) => {
    if (event && isWebPlatform && event.stopPropagation) {
      event.stopPropagation();
    }
    if (radio) {
      unoRadioRef.current = radio;
    }
    try {
      await stopRadio();
    } catch (error) {
      console.error('UNO kartı açılırken radyo durdurulamadı:', error);
    }
    setShowRoomList(false);
    setShowChat(false);
    setSelectedRoom(null);
    setSocialRadioId(null);
    setSelectedUnoRoom(null);
    setShowUnoChat(false);
    setShowUnoRoomList(true);
  }, [stopRadio]);

  const handleOpenBatak = useCallback(async (event, radio = null) => {
    if (event && isWebPlatform && event.stopPropagation) {
      event.stopPropagation();
    }
    if (radio) {
      batakRadioRef.current = radio;
    }
    try {
      await stopRadio();
    } catch (error) {
      console.error('Batak kartı açılırken radyo durdurulamadı:', error);
    }
    setShowRoomList(false);
    setShowChat(false);
    setSelectedRoom(null);
    setSocialRadioId(null);
    setSelectedBatakRoom(null);
    setShowBatakChat(false);
    setShowBatakRoomList(true);
  }, [stopRadio]);

  const handleSelectBatakRoom = useCallback(async (room) => {
    if (!room) {
      return;
    }
    try {
      await stopRadio();
    } catch (error) {
      console.error('Batak odasına girerken radyo durdurma hatası:', error);
    }
    const batakRoom = {
      ...room,
      gameType: 'batak',
      isGameRoom: true,
      radio: room.radio || batakRadioRef.current || batakRadio,
    };
    setSelectedBatakRoom(batakRoom);
    setShowBatakRoomList(false);
    setShowBatakChat(true);
  }, [stopRadio, batakRadio]);

  const handleCloseBatakRooms = useCallback(() => {
    setShowBatakRoomList(false);
  }, []);

  const handleCloseBatakChat = useCallback(async () => {
    setShowBatakChat(false);
    setSelectedBatakRoom(null);
    setShowBatakRoomList(true);
    try {
      await stopRadio();
    } catch (error) {
      console.error('Batak sohbetinden çıkarken radyo durdurulamadı:', error);
    }
  }, [stopRadio]);


  const handleSelectPistiRoom = useCallback(async (room) => {
    if (!room) {
      return;
    }
    
    // Önce çalan radyoyu durdur
    try {
      await stopRadio();
    } catch (error) {
      console.error('Pişti odasına girerken radyo durdurma hatası:', error);
    }
    
    // Pişti oyunu için gameType ekle
    const pistiRoom = {
      ...room,
      gameType: 'pisti',
      isGameRoom: true,
      // Eğer pistiRadioRef'te radyo varsa, onu ekle
      radio: room.radio || pistiRadioRef.current || null,
    };
    setSelectedPistiRoom(pistiRoom);
    setShowPistiRoomList(false);
    setShowPistiChat(true);
    
    // Radyo çalma işlemi ChatRoom.js içinde yapılacak (üst üste çalmayı önlemek için)
    // Sadece radyo bilgisini pistiRoom'a ekledik, ChatRoom useEffect'i radyoyu çalacak
  }, [playRadio, stopRadio]);

  const handleSelectUnoRoom = useCallback(async (room) => {
    if (!room) {
      return;
    }
    // Önce çalan radyoyu durdur
    try {
      await stopRadio();
    } catch (error) {
      console.error('UNO odasına girerken radyo durdurma hatası:', error);
    }
    const unoRoom = {
      ...room,
      gameType: 'uno',
      isGameRoom: true,
      // Eğer unoRadioRef'te radyo varsa, onu ekle
      radio: room.radio || unoRadioRef.current || unoRadio,
    };
    setSelectedUnoRoom(unoRoom);
    setShowUnoRoomList(false);
    setShowUnoChat(true);
    
    // Radyo çalma işlemi ChatRoom.js içinde yapılacak (üst üste çalmayı önlemek için)
    // Sadece radyo bilgisini unoRoom'a ekledik, ChatRoom useEffect'i radyoyu çalacak
  }, [stopRadio, unoRadio]);

  const handleClosePistiRooms = useCallback(() => {
    setShowPistiRoomList(false);
  }, []);

  const handleClosePistiChat = useCallback(async () => {
    setShowPistiChat(false);
    setSelectedPistiRoom(null);
    setShowPistiRoomList(true); // Oda listesine dön
    try {
      await stopRadio();
    } catch (error) {
      console.error('Pişti sohbetinden çıkarken radyo durdurulamadı:', error);
    }
  }, [stopRadio]);

  const handleCloseUnoRooms = useCallback(() => {
    setShowUnoRoomList(false);
  }, []);

  const handleCloseUnoChat = useCallback(async () => {
    setShowUnoChat(false);
    setSelectedUnoRoom(null);
    setShowUnoRoomList(true);
    try {
      await stopRadio();
    } catch (error) {
      console.error('UNO sohbetinden çıkarken radyo durdurulamadı:', error);
    }
  }, [stopRadio]);

  // Deep link handling (sadece Android için)
  const handleDeepLink = useCallback(({ url }) => {
    if (!url || Platform.OS !== 'android') return;
    
    try {
      // URL formatları:
      // samata://game/pisti
      // samata://game/uno
      // samata://game/batak
      
      let gameId = null;
      
      // samata://game/pisti formatını parse et
      if (url.startsWith('samata://game/')) {
        gameId = url.replace('samata://game/', '').split('/')[0];
      }
      
      if (gameId) {
        if (gameId === 'pisti') {
          handleOpenPisti({ stopPropagation: () => {} });
        } else if (gameId === 'uno') {
          handleOpenUno({ stopPropagation: () => {} });
        } else if (gameId === 'batak') {
          handleOpenBatak({ stopPropagation: () => {} });
        }
      }
    } catch (error) {
      console.error('Deep link işleme hatası:', error);
    }
  }, [handleOpenPisti, handleOpenUno, handleOpenBatak]);

  // Play Store referrer handling (uygulama açıldığında referrer parametresini kontrol et)
  useEffect(() => {
    if (Platform.OS !== 'android') return;
    
    // Play Store referrer parametresini kontrol et
    const checkReferrer = async () => {
      try {
        // Android'de referrer bilgisi Intent extras'ından alınır
        // Expo'da bu bilgiyi almak için expo-linking veya native modül gerekir
        // Şimdilik deep link handling yeterli, referrer için native modül gerekebilir
      } catch (error) {
        console.error('Referrer kontrol hatası:', error);
      }
    };
    
    // Uygulama açıldığında referrer'ı kontrol et
    checkReferrer();
  }, []);

  // Deep link listener (sadece Android için)
  useEffect(() => {
    if (Platform.OS !== 'android') return;
    
    // Uygulama açıkken gelen linkler
    const subscription = Linking.addEventListener('url', handleDeepLink);
    
    // Uygulama kapalıyken gelen linkler (app açıldığında kontrol et)
    Linking.getInitialURL().then((url) => {
      if (url) {
        handleDeepLink({ url });
      }
    }).catch((err) => {
      console.error('Deep link initial URL hatası:', err);
    });
    
    return () => {
      subscription.remove();
    };
  }, [handleDeepLink]);

  const handlePistiStateUpdate = useCallback((nextState) => {
    setPistiState(nextState);
  }, []);

  const pistiChatIntegration = useMemo(() => ({
    isActive: true,
    onStateUpdate: handlePistiStateUpdate,
  }), [handlePistiStateUpdate]);

  const handleUnoStateUpdate = useCallback((nextState) => {
    setUnoState(nextState);
  }, []);

  const unoChatIntegration = useMemo(() => ({
    isActive: true,
    onStateUpdate: handleUnoStateUpdate,
  }), [handleUnoStateUpdate]);

  const handleBatakStateUpdate = useCallback((nextState) => {
    setBatakState(nextState);
  }, []);

  const batakChatIntegration = useMemo(() => ({
    isActive: true,
    onStateUpdate: handleBatakStateUpdate,
  }), [handleBatakStateUpdate]);


  // RadioCard component for hover state
  const RadioCard = React.memo(({ item, isFavorite, playingRadio, listenerCounts, toggleFavorite, handleRadioSelect, handleOpenChat }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <View
        onMouseEnter={() => isWebPlatform && setIsHovered(true)}
        onMouseLeave={() => isWebPlatform && setIsHovered(false)}
      >
        <Card
          style={[
            styles.card,
            isWebPlatform && isHovered && styles.cardHovered,
            isWebPlatform && isMobile && {
              padding: 16,
              borderRadius: 12,
            },
            isWebPlatform && isTablet && {
              padding: 20,
              borderRadius: 16,
            },
          ]}
        >
        <Card.Content>
          <View style={styles.radioHeader}>
            <Title style={[
              styles.radioTitle,
              isWebPlatform && isMobile && { fontSize: 18 },
            ]}>{item.name}</Title>
            {playingRadio === item._id && (
              <View style={styles.playingIndicator}>
                <ActivityIndicator size="small" color="#6200ee" />
              </View>
            )}
          </View>
          {item.description && (
            <Paragraph style={[
              styles.radioDescription,
              isWebPlatform && isMobile && { fontSize: 12, marginTop: 8 },
            ]}>{item.description}</Paragraph>
          )}
          <View style={styles.radioFooter}>
            <View style={styles.footerLeft}>
              {item.displayCategory && (
                <Chip style={styles.categoryChip} textStyle={styles.categoryText}>
                  {item.displayCategory}
                </Chip>
              )}
            </View>
            <View style={styles.footerActions}>
              <View style={styles.favoriteWrapper}>
                <TouchableOpacity
                  onPress={(e) => {
                    e.stopPropagation();
                    toggleFavorite(item);
                  }}
                  style={styles.favoriteButton}
                  activeOpacity={0.7}
                >
                  <Text style={{ fontSize: 20, color: isFavorite ? '#4CAF50' : '#757575' }}>
                    {isFavorite ? '❤️' : '🤍'}
                  </Text>
                </TouchableOpacity>
                <Text style={styles.listenerCountText}>
                  {listenerCounts[item._id] ?? 0}
                </Text>
              </View>
              <View style={styles.listenButtonGroup}>
                <Chip
                  style={styles.soloListenChip}
                  textStyle={styles.soloListenChipText}
                  onPress={(e) => {
                    e.stopPropagation();
                    handleRadioSelect(item);
                  }}
                >
                  Yalnız Dinle
                </Chip>
                <Chip
                  style={styles.chatChip}
                  textStyle={styles.chatChipText}
                  onPress={(e) => {
                    // Web'de sayfa yenilenmesini önle (sadece stopPropagation)
                    if (e && isWebPlatform && e.stopPropagation) {
                      e.stopPropagation();
                    }
                    handleOpenChat(item, e);
                  }}
                >
                  Birlikte Dinle
                </Chip>
              </View>
            </View>
          </View>
        </Card.Content>
      </Card>
      </View>
    );
  });

  const renderRadioItem = ({ item }) => {
    const isFavorite = favoriteIds.has(item._id);

    return (
      <RadioCard
        item={item}
        isFavorite={isFavorite}
        playingRadio={playingRadio}
        listenerCounts={listenerCounts}
        toggleFavorite={toggleFavorite}
        handleRadioSelect={handleRadioSelect}
        handleOpenChat={handleOpenChat}
      />
    );
  };

  const renderCompactRadioItem = ({ item }) => {
    const isFavorite = favoriteIds.has(item._id);
    const listenerCount = listenerCounts[item._id] !== undefined ? listenerCounts[item._id] : 0;

    return (
      <Card
        style={[
          styles.compactCard,
          playingRadio === item._id && styles.compactCardPlaying,
        ]}
      >
        <Card.Content>
          <View style={styles.compactHeaderRow}>
            <Paragraph style={styles.compactTitle} numberOfLines={1}>
              {item.name}
            </Paragraph>
            <TouchableOpacity
              onPress={(e) => {
                e.stopPropagation();
                toggleFavorite(item);
              }}
              style={styles.compactFavoriteButton}
              activeOpacity={0.7}
            >
              <Text style={{ fontSize: 18, color: isFavorite ? '#4CAF50' : '#757575' }}>
                {isFavorite ? '❤️' : '🤍'}
              </Text>
            </TouchableOpacity>
          </View>
          {item.category && (
            <Paragraph style={styles.compactCategory} numberOfLines={1}>
              {item.category}
            </Paragraph>
          )}
          <View style={styles.compactFooter}>
            <Paragraph style={styles.compactListeners}>{listenerCount} dinleyici</Paragraph>
            <View style={styles.compactActions}>
              {playingRadio === item._id && (
                <ActivityIndicator size="small" color="#6200ee" style={styles.compactPlayIndicator} />
              )}
              <View style={styles.compactListenButtonGroup}>
                <Chip
                  style={styles.compactSoloListenChip}
                  textStyle={styles.compactSoloListenChipText}
                  onPress={(e) => {
                    e.stopPropagation();
                    handleRadioSelect(item);
                  }}
                >
                  Yalnız Dinle
                </Chip>
                <Chip
                  style={styles.compactChatChip}
                  textStyle={styles.compactChatChipText}
                  onPress={(e) => {
                    e.stopPropagation();
                    handleOpenChat(item, e);
                  }}
                >
                  Birlikte Dinle
                </Chip>
              </View>
            </View>
          </View>
        </Card.Content>
      </Card>
    );
  };

  // Favori item component'i - hook kullanabilmek için
  const FavoriteItem = ({ item, handleRadioSelect, handleOpenChat, toggleFavorite }) => {
    const [menuVisible, setMenuVisible] = useState(false);

    return (
      <Menu
        visible={menuVisible}
        onDismiss={() => setMenuVisible(false)}
        anchor={
          <Chip
            style={styles.favoriteChip}
            textStyle={styles.favoriteChipText}
            onPress={() => setMenuVisible(true)}
            onLongPress={() => toggleFavorite(item)}
          >
            {item.name}
          </Chip>
        }
      >
        <Menu.Item
          onPress={async () => {
            setMenuVisible(false);
            await handleRadioSelect(item);
          }}
          title="Yalnız Dinle"
        />
        <Menu.Item
          onPress={async (e) => {
            // Web'de sayfa yenilenmesini önle (sadece stopPropagation)
            if (e && isWebPlatform && e.stopPropagation) {
              e.stopPropagation();
            }
            setMenuVisible(false);
            await handleOpenChat(item, e);
          }}
          title="Birlikte Dinle"
        />
      </Menu>
    );
  };

  // Son dinlenen item component'i - hook kullanabilmek için
  const RecentItem = ({ item, handleRadioSelect, handleOpenChat }) => {
    const [menuVisible, setMenuVisible] = useState(false);

    return (
      <Menu
        visible={menuVisible}
        onDismiss={() => setMenuVisible(false)}
        anchor={
          <Chip
            style={styles.recentChip}
            textStyle={styles.recentChipText}
            onPress={() => setMenuVisible(true)}
          >
            {item.name}
          </Chip>
        }
      >
        <Menu.Item
          onPress={async () => {
            setMenuVisible(false);
            await handleRadioSelect(item);
          }}
          title="Yalnız Dinle"
        />
        <Menu.Item
          onPress={async (e) => {
            // Web'de sayfa yenilenmesini önle (sadece stopPropagation)
            if (e && isWebPlatform && e.stopPropagation) {
              e.stopPropagation();
            }
            setMenuVisible(false);
            await handleOpenChat(item, e);
          }}
          title="Birlikte Dinle"
        />
      </Menu>
    );
  };

  const renderFavoriteItem = ({ item }) => (
    <FavoriteItem 
      item={item} 
      handleRadioSelect={handleRadioSelect}
      handleOpenChat={handleOpenChat}
      toggleFavorite={toggleFavorite}
    />
  );

  const renderRecentItem = ({ item }) => (
    <RecentItem 
      item={item} 
      handleRadioSelect={handleRadioSelect}
      handleOpenChat={handleOpenChat}
    />
  );

  const canRenderUI = fontsLoaded || fontsError || isWebPlatform;

  if (!canRenderUI) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#E94560" />
      </View>
    );
  }

  // Pişti oyun oda listesi ekranı
  if (showPistiRoomList) {
    return (
      <PaperProvider theme={darkTheme} settings={paperSettings}>
        <ProfileModal
          visible={showProfileModal}
          profile={profileDraft}
          onChangeProfile={setProfileDraft}
          onDismiss={handleCloseProfile}
          onSave={handleSaveProfile}
          tokens={tokens}
          onShowRewardedAd={showRewardedAd}
          isRewardedLoading={isRewardedAdLoading}
          nicknameLocked={profileDraft.nicknameLocked}
        />
        <GameRoomList
          userName={userName}
          gameId={PISTI_GAME.id}
          onClose={handleClosePistiRooms}
          onSelectRoom={handleSelectPistiRoom}
          tokens={tokens}
          spendTokens={spendTokens}
          showRewardedAd={showRewardedAd}
          isAdsenseConfigured={isAdsenseConfigured}
        />
      </PaperProvider>
    );
  }

  if (showUnoRoomList) {
    return (
      <PaperProvider theme={darkTheme} settings={paperSettings}>
        <ProfileModal
          visible={showProfileModal}
          profile={profileDraft}
          onChangeProfile={setProfileDraft}
          onDismiss={handleCloseProfile}
          onSave={handleSaveProfile}
          tokens={tokens}
          onShowRewardedAd={showRewardedAd}
          isRewardedLoading={isRewardedAdLoading}
          nicknameLocked={profileDraft.nicknameLocked}
        />
        <GameRoomList
          userName={userName}
          gameId={UNO_GAME.id}
          onClose={handleCloseUnoRooms}
          onSelectRoom={handleSelectUnoRoom}
          tokens={tokens}
          spendTokens={spendTokens}
          showRewardedAd={showRewardedAd}
          isAdsenseConfigured={isAdsenseConfigured}
        />
      </PaperProvider>
    );
  }

  if (showBatakRoomList) {
    return (
      <PaperProvider theme={darkTheme} settings={paperSettings}>
        <ProfileModal
          visible={showProfileModal}
          profile={profileDraft}
          onChangeProfile={setProfileDraft}
          onDismiss={handleCloseProfile}
          onSave={handleSaveProfile}
          tokens={tokens}
          onShowRewardedAd={showRewardedAd}
          isRewardedLoading={isRewardedAdLoading}
          nicknameLocked={profileDraft.nicknameLocked}
        />
        <GameRoomList
          userName={userName}
          gameId={BATAK_GAME.id}
          onClose={handleCloseBatakRooms}
          onSelectRoom={handleSelectBatakRoom}
          tokens={tokens}
          spendTokens={spendTokens}
          showRewardedAd={showRewardedAd}
          isAdsenseConfigured={isAdsenseConfigured}
        />
      </PaperProvider>
    );
  }



  // Pişti sohbet ekranını göster
  if (showPistiChat && selectedPistiRoom) {
    // Eğer radyo bilgisi varsa (RoomList'ten geliyorsa), gerçek radyo objesini kullan
    const pistiGameRadio = selectedPistiRoom.radio || pistiRadio;
    return (
      <PaperProvider theme={darkTheme} settings={paperSettings}>
        <ProfileModal
          visible={showProfileModal}
          profile={profileDraft}
          onChangeProfile={setProfileDraft}
          onDismiss={handleCloseProfile}
          onSave={handleSaveProfile}
          tokens={tokens}
          onShowRewardedAd={showRewardedAd}
          isRewardedLoading={isRewardedAdLoading}
          nicknameLocked={profileDraft.nicknameLocked}
        />
        <ChatRoom
          radio={pistiGameRadio}
          room={selectedPistiRoom}
          userName={userName}
          userAvatar={userAvatar}
          onClose={handleClosePistiChat}
          gameIntegration={pistiChatIntegration}
          initialGameState={pistiState}
        />
      </PaperProvider>
    );
  }

  if (showUnoChat && selectedUnoRoom) {
    const unoGameRadio = selectedUnoRoom.radio || unoRadio;
    return (
      <PaperProvider theme={darkTheme} settings={paperSettings}>
        <ProfileModal
          visible={showProfileModal}
          profile={profileDraft}
          onChangeProfile={setProfileDraft}
          onDismiss={handleCloseProfile}
          onSave={handleSaveProfile}
          tokens={tokens}
          onShowRewardedAd={showRewardedAd}
          isRewardedLoading={isRewardedAdLoading}
          nicknameLocked={profileDraft.nicknameLocked}
        />
        <ChatRoom
          radio={unoGameRadio}
          room={selectedUnoRoom}
          userName={userName}
          userAvatar={userAvatar}
          onClose={handleCloseUnoChat}
          gameIntegration={unoChatIntegration}
          initialGameState={unoState}
        />
      </PaperProvider>
    );
  }

  if (showBatakChat && selectedBatakRoom) {
    const batakGameRadio = selectedBatakRoom.radio || batakRadio;
    return (
      <PaperProvider theme={darkTheme} settings={paperSettings}>
        <ProfileModal
          visible={showProfileModal}
          profile={profileDraft}
          onChangeProfile={setProfileDraft}
          onDismiss={handleCloseProfile}
          onSave={handleSaveProfile}
          tokens={tokens}
          onShowRewardedAd={showRewardedAd}
          isRewardedLoading={isRewardedAdLoading}
          nicknameLocked={profileDraft.nicknameLocked}
        />
        <ChatRoom
          radio={batakGameRadio}
          room={selectedBatakRoom}
          userName={userName}
          userAvatar={userAvatar}
          onClose={handleCloseBatakChat}
          gameIntegration={batakChatIntegration}
          initialGameState={batakState}
        />
      </PaperProvider>
    );
  }



  // Oda listesi ekranını göster
  if (showRoomList && socialRadioId) {
    if (isWebPlatform) {
      console.log('[Web] Rendering room list, socialRadioId:', socialRadioId, 'showRoomList:', showRoomList);
    }
    // Ana radios array'inde bul, bulunamazsa favoriler veya son dinlenenler listesinde ara
    let currentRadio = radios.find(r => r._id === socialRadioId);
    if (!currentRadio) {
      // Favoriler listesinde ara
      currentRadio = favorites.find(r => r._id === socialRadioId);
    }
    if (!currentRadio) {
      // Son dinlenenler listesinde ara
      currentRadio = recentRadios.find(r => r._id === socialRadioId);
    }
    // Hala bulunamazsa, name'e göre ara
    if (!currentRadio) {
      const radioById = [...favorites, ...recentRadios].find(r => r._id === socialRadioId);
      if (radioById) {
        currentRadio = radios.find(r => r.name === radioById.name) || radioById;
      }
    }
    // currentRadio bulunamazsa fallback göster
    if (!currentRadio) {
      if (isWebPlatform) {
        console.error('[Web] Radio not found for socialRadioId:', socialRadioId);
        console.log('[Web] Available radios:', radios.map(r => ({ id: r._id, name: r.name })));
      }
      return (
        <PaperProvider theme={darkTheme} settings={paperSettings}>
          <View style={[styles.container, { justifyContent: 'center', alignItems: 'center', padding: 20 }]}>
            <Text style={{ color: '#FFFFFF', fontSize: 18, marginBottom: 10, textAlign: 'center' }}>
              Radyo bulunamadı
            </Text>
            <TouchableOpacity
              onPress={() => {
                setShowRoomList(false);
                setSocialRadioId(null);
              }}
              style={{
                backgroundColor: '#E94560',
                paddingHorizontal: 20,
                paddingVertical: 10,
                borderRadius: 8,
              }}
            >
              <Text style={{ color: '#FFFFFF', fontSize: 16 }}>Ana Sayfaya Dön</Text>
            </TouchableOpacity>
          </View>
        </PaperProvider>
      );
    }
    
    if (!fontsLoaded && !fontsError) {
      return (
        <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
          <ActivityIndicator size="large" color="#E94560" />
        </View>
      );
    }
    
    if (isWebPlatform) {
      console.log('[Web] Rendering RoomList component for radio:', currentRadio.name);
    }
    
    return (
      <PaperProvider theme={darkTheme} settings={paperSettings}>
        <ProfileModal
          visible={showProfileModal}
          profile={profileDraft}
          onChangeProfile={setProfileDraft}
          onDismiss={handleCloseProfile}
          onSave={handleSaveProfile}
          tokens={tokens}
          onShowRewardedAd={showRewardedAd}
          isRewardedLoading={isRewardedAdLoading}
          nicknameLocked={profileDraft.nicknameLocked}
        />
        <RoomList
          radio={currentRadio}
          userName={userName}
          tokens={tokens}
          spendTokens={spendTokens}
          showRewardedAd={showRewardedAd}
          isAdsenseConfigured={isAdsenseConfigured}
          onPlayRadio={playRadio}
              onOpenPisti={handleOpenPisti}
              onOpenBatak={handleOpenBatak}
              onOpenUno={handleOpenUno}
          onClose={() => {
            setShowRoomList(false);
            setSelectedRoom(null);
            setSocialRadioId(null);
          }}
          onSelectRoom={(room) => {
            // Oyun odası kontrolü
            if (room.isGameRoom) {
              // Oyun odası için özel işlem - Category ve Mystery oyunları kaldırıldı
              // Sadece Pişti, UNO, Batak ve OKEY oyunları destekleniyor
            } else {
              // Normal oda
              if (isWebPlatform) {
                console.log('[Web] Selecting normal room:', room?.roomId, 'roomName:', room?.roomName);
                console.log('[Web] Current state before update - socialRadioId:', socialRadioId, 'showRoomList:', showRoomList, 'currentRadio._id:', currentRadio?._id);
              }
              // socialRadioId'yi tekrar set et (güvenlik için)
              setSocialRadioId(currentRadio._id);
              setSelectedRoom(room);
              setShowRoomList(false);
              setShowChat(true);
              if (isWebPlatform) {
                console.log('[Web] State updated - showChat: true, selectedRoom:', room?.roomId, 'socialRadioId set to:', currentRadio._id);
              }
            }
          }}
        />
      </PaperProvider>
    );
  }

  // Sohbet ekranını göster
  if (isWebPlatform) {
    console.log('[Web] Chat render check - showChat:', showChat, 'socialRadioId:', socialRadioId, 'selectedRoom:', selectedRoom?.roomId);
  }
  
  if (showChat && socialRadioId && selectedRoom) {
    if (isWebPlatform) {
      console.log('[Web] Rendering chat room, socialRadioId:', socialRadioId, 'selectedRoom:', selectedRoom?.roomId);
    }
    const currentRadio = radios.find(r => r._id === socialRadioId) || 
                         favorites.find(r => r._id === socialRadioId) ||
                         recentRadios.find(r => r._id === socialRadioId);
    
    if (!currentRadio) {
      if (isWebPlatform) {
        console.error('[Web] Radio not found for chat, socialRadioId:', socialRadioId);
      }
      return (
        <PaperProvider theme={darkTheme} settings={paperSettings}>
          <View style={[styles.container, { justifyContent: 'center', alignItems: 'center', padding: 20 }]}>
            <Text style={{ color: '#FFFFFF', fontSize: 18, marginBottom: 10, textAlign: 'center' }}>
              Radyo bulunamadı
            </Text>
            <TouchableOpacity
              onPress={() => {
                setShowChat(false);
                setSelectedRoom(null);
                setSocialRadioId(null);
              }}
              style={{
                backgroundColor: '#E94560',
                paddingHorizontal: 20,
                paddingVertical: 10,
                borderRadius: 8,
              }}
            >
              <Text style={{ color: '#FFFFFF', fontSize: 16 }}>Ana Sayfaya Dön</Text>
            </TouchableOpacity>
          </View>
        </PaperProvider>
      );
    }
    
    if (!fontsLoaded && !fontsError) {
      return (
        <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
          <ActivityIndicator size="large" color="#E94560" />
        </View>
      );
    }
    
    if (isWebPlatform) {
      console.log('[Web] Rendering ChatRoom component');
    }
    
    return (
      <PaperProvider theme={darkTheme} settings={paperSettings}>
        <ProfileModal
          visible={showProfileModal}
          profile={profileDraft}
          onChangeProfile={setProfileDraft}
          onDismiss={handleCloseProfile}
          onSave={handleSaveProfile}
          tokens={tokens}
          onShowRewardedAd={showRewardedAd}
          isRewardedLoading={isRewardedAdLoading}
          nicknameLocked={profileDraft.nicknameLocked}
        />
        <ChatRoom
          radio={currentRadio}
          room={selectedRoom}
          userName={userName}
          userAvatar={userAvatar}
          onClose={async () => {
            setShowChat(false);
            setSelectedRoom(null);
            setShowRoomList(true); // Oda listesine dön
            await stopRadio();
          }}
        />
      </PaperProvider>
    );
  }

  // Sayfa görüntüleme kontrolleri
  if (showAboutPage) {
    return (
      <PaperProvider theme={darkTheme} settings={paperSettings}>
        <AboutPage onClose={closeAboutPage} />
      </PaperProvider>
    );
  }

  if (showContactPage) {
    return (
      <PaperProvider theme={darkTheme} settings={paperSettings}>
        <ContactPage onClose={closeContactPage} />
      </PaperProvider>
    );
  }

  if (showPrivacyPolicyPage) {
    return (
      <PaperProvider theme={darkTheme} settings={paperSettings}>
        <PrivacyPolicyPage onClose={closePrivacyPolicyPage} />
      </PaperProvider>
    );
  }

  // Font yüklenene kadar bekle (web'de daha esnek)
  if (!fontsLoaded && !fontsError && !isWebPlatform) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator size="large" color="#E94560" />
      </View>
    );
  }

  // Web debug
  if (isWebPlatform) {
    console.log('[Web Debug] Rendering main UI...', { fontsLoaded, fontsError, canRenderUI: fontsLoaded || fontsError || isWebPlatform });
  }

  return (
    <PaperProvider theme={darkTheme} settings={paperSettings}>
      <ProfileModal
        visible={showProfileModal}
        profile={profileDraft}
        onChangeProfile={setProfileDraft}
        onDismiss={handleCloseProfile}
        onSave={handleSaveProfile}
        tokens={tokens}
        onShowRewardedAd={showRewardedAd}
        isRewardedLoading={isRewardedAdLoading}
        nicknameLocked={profileDraft.nicknameLocked}
      />
      <View style={[
        styles.container,
        isWebPlatform && isMobile && {
          width: '100%',
          maxWidth: '100%',
          paddingHorizontal: 12,
        },
        isWebPlatform && isTablet && {
          paddingHorizontal: 20,
        }
      ]}>
        <StatusBar style="light" hidden />
        
        <View style={[
          styles.header,
          isWebPlatform && isMobile && {
            paddingHorizontal: 12,
            paddingTop: 20,
            paddingBottom: 16,
            marginTop: 0,
            marginBottom: 12,
            borderRadius: 0,
          },
          isWebPlatform && isTablet && {
            paddingHorizontal: 20,
            paddingTop: 28,
            paddingBottom: 24,
            marginBottom: 20,
          }
        ]}>
          <View style={styles.headerRow}>
            <Title style={[
              styles.appTitle,
              isWebPlatform && isMobile && { fontSize: 28 },
              isWebPlatform && isTablet && { fontSize: 32 },
            ]}>Şamata</Title>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              {isWebPlatform ? (
                // Web için basit dropdown menü - TEST: v2
                <View ref={menuContainerRef} style={{ position: 'relative', zIndex: 1000 }}>
                  <TouchableOpacity
                    onPress={() => {
                      console.log('[Web Menu] Button clicked, current state:', mainMenuVisible);
                      setMainMenuVisible(!mainMenuVisible);
                    }}
                    style={[
                      styles.menuButton,
                      {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        borderWidth: 2,
                        borderColor: '#FFFFFF',
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: 0.5,
                        shadowRadius: 4,
                        elevation: 5,
                      }
                    ]}
                    activeOpacity={0.7}
                    accessibilityLabel="Menü"
                    accessibilityRole="button"
                  >
                    <MaterialCommunityIcons name="menu" size={28} color="#FFFFFF" />
                  </TouchableOpacity>
                  {mainMenuVisible && (
                    <View 
                      style={{
                        position: 'absolute',
                        top: 40,
                        right: 0,
                        backgroundColor: '#1a1a1a',
                        borderRadius: 8,
                        padding: 8,
                        minWidth: 200,
                        zIndex: 1001,
                        borderWidth: 1,
                        borderColor: '#333333',
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 4 },
                        shadowOpacity: 0.3,
                        shadowRadius: 8,
                        elevation: 8,
                      }}
                    >
                      <TouchableOpacity
                        onPress={() => {
                          setMainMenuVisible(false);
                          openAboutPage();
                        }}
                        style={{
                          padding: 12,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 8,
                          borderRadius: 4,
                        }}
                        activeOpacity={0.7}
                      >
                        <MaterialCommunityIcons name="information" size={20} color="#FFFFFF" />
                        <Text style={{ color: '#FFFFFF', fontSize: 16 }}>Hakkımızda</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={() => {
                          setMainMenuVisible(false);
                          openContactPage();
                        }}
                        style={{
                          padding: 12,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 8,
                          borderRadius: 4,
                        }}
                        activeOpacity={0.7}
                      >
                        <MaterialCommunityIcons name="email" size={20} color="#FFFFFF" />
                        <Text style={{ color: '#FFFFFF', fontSize: 16 }}>İletişim</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={() => {
                          setMainMenuVisible(false);
                          openPrivacyPolicyPage();
                        }}
                        style={{
                          padding: 12,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 8,
                          borderRadius: 4,
                        }}
                        activeOpacity={0.7}
                      >
                        <MaterialCommunityIcons name="shield-lock" size={20} color="#FFFFFF" />
                        <Text style={{ color: '#FFFFFF', fontSize: 16 }}>Gizlilik Politikası</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              ) : (
                // Mobil için Paper Menu
                <Menu
                  visible={mainMenuVisible}
                  onDismiss={() => setMainMenuVisible(false)}
                  anchor={
                    <TouchableOpacity
                      onPress={() => setMainMenuVisible(true)}
                      style={styles.menuButton}
                      activeOpacity={0.7}
                    >
                      <MaterialCommunityIcons name="menu" size={24} color="#FFFFFF" />
                    </TouchableOpacity>
                  }
                >
                  <Menu.Item
                    onPress={() => {
                      setMainMenuVisible(false);
                      openAboutPage();
                    }}
                    title="Hakkımızda"
                    leadingIcon="information"
                  />
                  <Menu.Item
                    onPress={() => {
                      setMainMenuVisible(false);
                      openContactPage();
                    }}
                    title="İletişim"
                    leadingIcon="email"
                  />
                  <Menu.Item
                    onPress={() => {
                      setMainMenuVisible(false);
                      openPrivacyPolicyPage();
                    }}
                    title="Gizlilik Politikası"
                    leadingIcon="shield-lock"
                  />
                </Menu>
              )}
              <TouchableOpacity onPress={handleOpenProfile} style={styles.avatarButton} activeOpacity={0.7}>
                {renderAvatarBadge(
                  userAvatar,
                  40,
                  styles.profileAvatar,
                  styles.profileAvatarLabel,
                  userName?.[0] || 'K'
                )}
              </TouchableOpacity>
            </View>
          </View>
          <Paragraph style={[
            styles.greetingText,
            isWebPlatform && isMobile && { fontSize: 14, marginTop: 6 },
          ]}>Merhaba, {userName}!</Paragraph>
          <View style={styles.energyContainer}>
            <Text style={styles.energyLabel}>Sosyal Enerji</Text>
            <View style={styles.energyBar}>
              {Array.from({ length: 10 }).map((_, idx) => {
                const filled = idx < Math.min(tokens, 10);
                return <View key={idx} style={filled ? styles.energySegmentFilled : styles.energySegmentEmpty} />;
              })}
            </View>
            <Text style={styles.energyValue}>{tokens}</Text>
          </View>
        </View>

        <View style={[
          styles.categoriesContainer,
          isWebPlatform && isMobile && {
            paddingVertical: 12,
            paddingHorizontal: 12,
          },
          isWebPlatform && isTablet && {
            paddingVertical: 14,
            paddingHorizontal: 20,
          },
        ]}>
          <FlatList
            horizontal
            data={categories}
            keyExtractor={(item) => item}
            renderItem={({ item }) => (
              <Chip
                mode={selectedCategory === item ? 'flat' : 'outlined'}
                selected={selectedCategory === item}
                onPress={() => setSelectedCategory(item)}
                style={[
                  styles.categoryFilter,
                  isWebPlatform && isMobile && {
                    paddingVertical: 6,
                    paddingHorizontal: 12,
                    height: 36,
                  },
                ]}
                textStyle={[
                  styles.categoryFilterText,
                  isWebPlatform && isMobile && {
                    fontSize: 12,
                  },
                ]}
              >
                {getCategoryLabel(item)}
              </Chip>
            )}
            showsHorizontalScrollIndicator={isWebPlatform ? true : false}
            scrollEnabled={true}
            bounces={isWebPlatform ? true : false}
          />
        </View>

        {favorites.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Title style={styles.favoriteSectionTitle}>Favoriler</Title>
            </View>
            <FlatList
              data={favorites}
              renderItem={renderFavoriteItem}
              keyExtractor={(item) => item._id?.toString() || item.name}
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.favoriteList}
              extraData={{ favorites }}
            />
          </View>
        )}

        {recentRadios.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Title style={styles.recentSectionTitle}>En Son Dinlenenler</Title>
            </View>
            <FlatList
              data={recentRadios}
              renderItem={renderRecentItem}
              keyExtractor={(item) => item._id?.toString() || item.name}
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.favoriteList}
              extraData={{ recentRadios }}
            />
          </View>
        )}

        {/* AdSense için içerik bölümü */}
        {/* Hakkımızda içeriği AboutPage'e taşındı */}

        <FlatList
          data={filteredRadios}
          renderItem={renderRadioItem}
          keyExtractor={(item) => item._id?.toString() || item.name}
          contentContainerStyle={[
            styles.listContent,
            isWebPlatform && isMobile && {
              padding: 12,
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 12,
            },
            isWebPlatform && isTablet && {
              padding: 20,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 16,
            },
          ]}
          removeClippedSubviews
          windowSize={5}
          initialNumToRender={8}
          maxToRenderPerBatch={8}
          updateCellsBatchingPeriod={50}
          ListHeaderComponent={
            <GameCarousel
              stateRef={gameCarouselStateRef}
              pistiState={pistiState}
              unoState={unoState}
              batakState={batakState}
              onOpenPisti={handleOpenPisti}
              onOpenUno={handleOpenUno}
              onOpenBatak={handleOpenBatak}
            />
          }
          ListEmptyComponent={
            loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#6200ee" />
              </View>
            ) : (
              <View style={styles.emptyContainer}>
                <Paragraph style={styles.emptyText}>
                  Radyo bulunamadı
                </Paragraph>
              </View>
            )
          }
        />

        {/* Türk Pop Sanatçıları Bilgileri (radyo kartlarının altında) */}
        <TurkishPopArtists />

        {/* Pop top listeler (radyo kartlarının altında, oynatılabilir) */}
        <TopLists
          turkList={topTurkPop}
          yabanciList={topYabanciPop}
          onPlay={handleRadioSelect}
          onListenTogether={handleOpenChat}
          playingRadio={playingRadio}
          listenerCounts={listenerCounts}
        />

        {/* Özgün demo şarkı sözleri (telif içermeyen) */}
        <LyricsShowcase />

        {/* İçerik kaliteyi artıran editoryal bölüm (radyo kartları üstte kalır) */}
        <EditorialContent />

        {playingRadio && (
          <FAB
            icon="stop"
            style={styles.fab}
            onPress={stopRadio}
            label="Durdur"
          />
        )}
      </View>
    </PaperProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0F1115',
    ...(isWebPlatform ? {
      minHeight: '100vh',
      width: '100%',
      maxWidth: 1600,
      alignSelf: 'center',
      paddingHorizontal: 32,
    } : {}),
  },
  header: {
    backgroundColor: '#151A22',
    paddingTop: isWebPlatform ? 32 : 50,
    paddingBottom: isWebPlatform ? 32 : 20,
    paddingHorizontal: isWebPlatform ? 32 : 16,
    borderBottomWidth: 1,
    borderBottomColor: '#222835',
    ...(isWebPlatform ? {
      borderRadius: 16,
      marginTop: 24,
      marginBottom: 24,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    } : {}),
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  appTitle: {
    color: '#FF4444',
    fontSize: isWebPlatform ? 36 : 28,
    fontWeight: 'bold',
  },
  menuButton: {
    padding: 10,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.5)',
    minWidth: 44,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarButton: {
    borderRadius: 20,
    overflow: 'hidden',
  },
  profileAvatar: {
    borderRadius: 20,
  },
  profileAvatarLabel: {
    fontSize: 18,
  },
  greetingText: {
    marginTop: 8,
    color: '#B0B0B0',
    fontSize: isWebPlatform ? 16 : 14,
  },
  energyContainer: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  energyLabel: {
    color: '#A7B1C2',
    fontSize: 12,
  },
  energyBar: {
    flexDirection: 'row',
    gap: 2,
    marginLeft: 8,
    marginRight: 8,
  },
  energySegmentFilled: {
    width: 10,
    height: 8,
    borderRadius: 2,
    backgroundColor: '#00C2A8',
  },
  energySegmentEmpty: {
    width: 10,
    height: 8,
    borderRadius: 2,
    backgroundColor: '#222835',
  },
  energyValue: {
    color: '#E6EAF2',
    fontSize: 12,
    fontWeight: '600',
  },
  categoriesContainer: {
    paddingVertical: isWebPlatform ? 16 : 8,
    paddingHorizontal: isWebPlatform ? 24 : 16,
  },
  categoryFilter: {
    marginRight: 8,
    backgroundColor: '#2C2C2C',
    ...(isWebPlatform ? {
      paddingVertical: 8,
      paddingHorizontal: 16,
      height: 40,
    } : {}),
  },
  categoryFilterText: {
    fontSize: isWebPlatform ? 14 : 12,
    color: '#FFFFFF',
  },
  listContent: {
    padding: isWebPlatform ? 32 : 16,
    ...(isWebPlatform ? {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
      gap: 24,
    } : {}),
  },
  card: {
    marginBottom: isWebPlatform ? 0 : 12,
    elevation: 2,
    backgroundColor: '#1E1E1E',
    borderRadius: isWebPlatform ? 20 : 12,
    ...(isWebPlatform ? {
      padding: 24,
      borderWidth: 1,
      borderColor: '#2A2A2A',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
    } : {}),
  },
  cardHovered: {
    ...(isWebPlatform ? {
      transform: [{ translateY: -4 }],
      borderColor: '#FF4444',
      boxShadow: '0 12px 24px rgba(255, 68, 68, 0.15)',
      backgroundColor: '#242424',
    } : {}),
  },
  radioHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  radioTitle: {
    fontSize: isWebPlatform ? 22 : 18,
    fontWeight: 'bold',
    flex: 1,
    color: '#FFFFFF',
  },
  playingIndicator: {
    marginLeft: 8,
  },
  radioDescription: {
    marginTop: isWebPlatform ? 12 : 8,
    color: '#A7B1C2',
    fontSize: isWebPlatform ? 14 : 12,
  },
  radioFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  },
  footerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    flexWrap: 'wrap',
  },
  footerActions: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  listenButtonGroup: {
    marginLeft: 4,
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  categoryChip: {
    alignSelf: 'flex-start',
    backgroundColor: '#2C2C2C',
    marginRight: 6,
    marginBottom: 4,
  },
  categoryText: {
    fontSize: 11,
    color: '#FFFFFF',
  },
  listenerChip: {
    backgroundColor: '#FF4444',
    marginLeft: 4,
    marginBottom: 4,
  },
  listenerText: {
    color: '#000000',
    fontSize: 11,
  },
  soloListenChip: {
    backgroundColor: '#2C2C2C',
    borderColor: '#FF4444',
    borderWidth: 1,
    marginBottom: 6,
    alignSelf: 'flex-start',
  },
  soloListenChipText: {
    color: '#FF4444',
    fontSize: 11,
  },
  chatChip: {
    backgroundColor: '#FF4444',
    alignSelf: 'flex-start',
  },
  chatChipText: {
    color: '#FFFFFF',
    fontSize: 11,
  },
  favoriteButton: {
    margin: 0,
  },
  favoriteWrapper: {
    alignItems: 'center',
  },
  listenerCountText: {
    color: '#4CAF50',
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    padding: 32,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#B0B0B0',
    textAlign: 'center',
  },
  fab: {
    position: 'absolute',
    margin: isWebPlatform ? 24 : 16,
    right: 0,
    bottom: 0,
    backgroundColor: '#FF4444',
    ...(isWebPlatform ? {
      width: 64,
      height: 64,
    } : {}),
  },
  compactCard: {
    width: 200,
    marginRight: 12,
    borderRadius: 12,
    backgroundColor: '#1E1E1E',
  },
  compactCardPlaying: {
    borderWidth: 2,
    borderColor: '#FF4444',
  },
  compactHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  compactTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    flex: 1,
    marginRight: 8,
    color: '#FFFFFF',
  },
  compactFavoriteButton: {
    margin: 0,
  },
  compactCategory: {
    fontSize: 12,
    color: '#B0B0B0',
    marginTop: 4,
  },
  compactFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  },
  compactListeners: {
    fontSize: 12,
    color: '#B0B0B0',
  },
  compactActions: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  compactListenButtonGroup: {
    marginLeft: 4,
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  compactSoloListenChip: {
    backgroundColor: '#2C2C2C',
    borderColor: '#FF4444',
    borderWidth: 1,
    marginBottom: 6,
  },
  compactSoloListenChipText: {
    color: '#FF4444',
    fontSize: 10,
  },
  compactChatChip: {
    backgroundColor: '#FF4444',
    alignSelf: 'flex-start',
  },
  compactChatChipText: {
    color: '#FFFFFF',
    fontSize: 10,
  },
  compactPlayIndicator: {
    marginRight: 4,
  },
  aboutSection: {
    marginTop: isWebPlatform ? 32 : 24,
    paddingHorizontal: isWebPlatform ? 32 : 16,
    marginBottom: isWebPlatform ? 32 : 24,
  },
  aboutCard: {
    backgroundColor: '#1E1E1E',
    padding: isWebPlatform ? 24 : 16,
    borderRadius: isWebPlatform ? 16 : 12,
    marginTop: 16,
  },
  aboutText: {
    color: '#B0B0B0',
    fontSize: isWebPlatform ? 16 : 14,
    lineHeight: isWebPlatform ? 24 : 20,
    marginBottom: isWebPlatform ? 16 : 12,
    textAlign: 'left',
  },
  section: {
    marginTop: isWebPlatform ? 32 : 16,
    paddingHorizontal: isWebPlatform ? 32 : 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: isWebPlatform ? 0 : 16,
    marginBottom: isWebPlatform ? 20 : 8,
  },
  sectionTitle: {
    fontSize: isWebPlatform ? 24 : 18,
    fontWeight: 'bold',
    color: '#FF4444',
  },
  recentSectionTitle: {
    fontSize: isWebPlatform ? 24 : 18,
    fontWeight: 'bold',
    color: '#FF9800',
  },
  compactList: {
    paddingLeft: 16,
    paddingRight: 8,
    paddingBottom: 4,
  },
  favoriteChip: {
    marginRight: 8,
    backgroundColor: '#2C2C2C',
    borderColor: '#4CAF50',
    borderWidth: 1,
  },
  favoriteChipText: {
    fontSize: 14,
    color: '#4CAF50',
  },
  favoriteSectionTitle: {
    fontSize: isWebPlatform ? 24 : 18,
    fontWeight: 'bold',
    color: '#4CAF50',
  },
  favoriteList: {
    paddingLeft: 16,
    paddingRight: 8,
    paddingBottom: 4,
  },
  recentChip: {
    marginRight: 8,
    backgroundColor: '#2C2C2C',
    borderColor: '#FF9800',
    borderWidth: 1,
  },
  recentChipText: {
    fontSize: 14,
    color: '#FF9800',
  },
});

